import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Divider,
  TextField,
  MenuItem,
  Menu,
  CircularProgress,
  Chip,
  // Dialog imported as MobileDialog below
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  IconButton,
  Paper,
  Tooltip,
  Autocomplete,
  TableContainer,
} from '@mui/material';
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
} from '@mui/x-data-grid';
// Import DataGridPro for orders section
import { DataGridPro } from '@mui/x-data-grid-pro';
import ResponsiveDataView from '../components/ResponsiveDataView';
import ResponsiveToolbar, { ToolbarActions } from '../components/ResponsiveToolbar';
import { heIL } from '@mui/x-data-grid/locales';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import API from '../services/api';
import { useUser } from '../contexts/UserContext';
import { generateBusinessHeader, openPrintWindow } from '../utils/printUtils';
import { Snackbar } from '@mui/material';
import { format, startOfMonth, endOfMonth } from 'date-fns';

// ==========================
// 1) DatePicker imports
// ==========================
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import heLocale from 'date-fns/locale/he';

// ==========================
// 2) New Imports for comment editing
// ==========================
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PrintIcon from '@mui/icons-material/Print';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';

import ExportOrderItemsDialog from './dialogs/ExportOrderItemsDialog';
import MonthlyStatementDialog from './dialogs/MonthlyStatementDialog';
import OrdersMonthlyStatementDialog from './dialogs/OrdersMonthlyStatementDialog';
import SummarizeIcon from '@mui/icons-material/Summarize';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

// ==========================
// 3) Helper: 1 month back
// ==========================
import { useQueryClient, useQuery, keepPreviousData } from '@tanstack/react-query';

// ========== NEW: For show/hide password icon ==========
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import EditTransactionDialog from './dialogs/EditTransactionDialog';
import MobileDialog from './dialogs/MobileDialog';
import OrderStatusDialog from './dialogs/OrderStatusDialog';
import CloseIcon from '@mui/icons-material/Close';

function getOneMonthBackRange() {
  const now = new Date();
  // End date is "today" at 23:59:59.999
  const end = new Date(now.getTime());
  end.setHours(23, 59, 59, 999);

  // Start date is "one month ago" from today at 00:00:00
  const start = new Date(now.getTime());
  start.setMonth(start.getMonth() - 1);
  start.setHours(0, 0, 0, 0);

  return { start, end };
}

// ==========================
// Date Cache Helper Functions
// ==========================

// Get today's date as YYYY-MM-DD string for cache validation
const getTodayDateString = () => {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
};

// Get cached date range with "new day" validation
// Returns null dates if cache is from a different day or doesn't exist
const getDateCacheWithValidation = (cacheKey) => {
  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (!cached) return { startDate: null, endDate: null };

    const parsed = JSON.parse(cached);
    const today = getTodayDateString();

    // If cached on a different day, clear cache and return nulls
    if (parsed.cachedOn !== today) {
      sessionStorage.removeItem(cacheKey);
      return { startDate: null, endDate: null };
    }

    // Same day - restore the cached dates
    return {
      startDate: parsed.startDate ? new Date(parsed.startDate) : null,
      endDate: parsed.endDate ? new Date(parsed.endDate) : null,
    };
  } catch (e) {
    console.error('Failed to load date cache from sessionStorage', e);
    return { startDate: null, endDate: null };
  }
};

// Save date range to cache with "cachedOn" timestamp
const saveDateCache = (cacheKey, startDate, endDate) => {
  try {
    const cacheData = {
      startDate: startDate instanceof Date && !isNaN(startDate)
        ? format(startDate, 'yyyy-MM-dd')
        : null,
      endDate: endDate instanceof Date && !isNaN(endDate)
        ? format(endDate, 'yyyy-MM-dd')
        : null,
      cachedOn: getTodayDateString(),
    };
    sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));
  } catch (e) {
    console.error('Failed to save date cache to sessionStorage', e);
  }
};

const theme = createTheme({
  direction: 'rtl',
  ...heIL,
});

function CustomToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
    </GridToolbarContainer>
  );
}

// ==========================
// NEW: Order Detail Panel Component
// ==========================
// Fetch order details for the expandable panel
const fetchOrderDetails = async (orderId) => {
  try {
    const response = await API.get(`/order/?OrderID=${orderId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching order details:', error);
    throw error;
  }
};

// Fetch orders for autocomplete with server-side search (constrained to a client)
const fetchOrdersForAutocompleteAPI = async (businessId, searchTerm = '') => {
  if (!businessId) return [];

  const params = {
    light: true,
    limit: 100,
    ClientBusinessID: businessId,
  };

  if (searchTerm) {
    params.search = searchTerm;
  }

  const response = await API.get('/orders/', { params });
  return response.data;
};

// Order detail panel component - simplified to show only products
function OrderDetailPanel({ orderId, generalSchema, orderItemFields, orderItemFieldsSchema }) {
  const productLabel = generalSchema?.product?.label || 'מוצר';
  const productPluralLabel = generalSchema?.product?.plural_label || 'מוצרים';
  
  const { data: orderDetails, isLoading, isError } = useQuery({
    queryKey: ['orderDetail', orderId],
    queryFn: () => fetchOrderDetails(orderId),
    enabled: !!orderId,
    staleTime: 5 * 60 * 1000,
  });

  // Get storage configuration from user context
  const { userProfile } = useUser();
  const role = userProfile?.role;
  const businessProfile = userProfile?.group?.business_profile || {};
  const orderConfig = userProfile?.group?.order_config || {};
  const storageConfig = orderConfig.storage_config || {};
  const storageEnabled = storageConfig.enabled || false;

  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={30} />
      </Box>
    );
  }

  if (isError || !orderDetails) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        שגיאה בטעינת פרטי ההזמנה
      </Alert>
    );
  }

  const hasItems = orderDetails.items && orderDetails.items.length > 0;

  if (!hasItems) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        אין {productPluralLabel} בהזמנה זו
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2 }}>
        <Table>
          <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
            <TableRow>
               <TableCell align="center" width="50px">#</TableCell>
              <TableCell>שם {productLabel}</TableCell>
              <TableCell align="center">כמות</TableCell>
              {storageEnabled && <TableCell align="center">מחסן</TableCell>}
              <TableCell align="right">מחיר יחידה</TableCell>
              <TableCell align="right">סה"כ</TableCell>
              {orderItemFields.includes("Status") && <TableCell align="center">סטטוס</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {orderDetails.items.map((item, index) => (
              <TableRow key={item.OrderItemID} hover>
              <TableCell align="center" width="50px" sx={{ fontWeight: 'bold' }}>
                {index + 1}
              </TableCell>
                <TableCell>{item.ProductName}</TableCell>
                <TableCell align="center">{item.Quantity}</TableCell>
                {storageEnabled && (
                  <TableCell align="center">
                    {item.StorageName || 'ברירת מחדל'}
                  </TableCell>
                )}
                <TableCell align="right">
                  {item.PriceOverridden && item.OriginalPrice ? (
                    <Box sx={{ display:'flex', alignItems:'center', justifyContent: 'flex-end', direction:'ltr' }}>
                      <Typography 
                        variant="body2" 
                        sx={{ textDecoration: 'line-through', color: 'text.secondary', mr: 1 }}
                      >
                        {new Intl.NumberFormat('he-IL', {
                          style: 'currency',
                          currency: 'ILS',
                        }).format(item.OriginalPrice)}
                      </Typography>
                      <Typography>
                        {new Intl.NumberFormat('he-IL', {
                          style: 'currency',
                          currency: 'ILS',
                        }).format(item.UnitPrice)}
                      </Typography>
                    </Box>
                  ) : (
                    new Intl.NumberFormat('he-IL', {
                      style: 'currency',
                      currency: 'ILS',
                    }).format(item.UnitPrice)
                  )}
                </TableCell>
                <TableCell align="right">
                  {new Intl.NumberFormat('he-IL', {
                    style: 'currency',
                    currency: 'ILS',
                  }).format(item.Subtotal)}
                </TableCell>
                {orderItemFields.includes("Status") && (
                  <TableCell align="center">
                    <Chip
                      label={item.StatusDisplay || (item.Status === 'supplied' ? 'סופק' : 'לא סופק')}
                      sx={{
                        backgroundColor: item.Status === 'supplied' ? '#e8f5e9' : '#ffebee',
                        color: item.Status === 'supplied' ? '#4caf50' : '#f44336',
                        borderColor: item.Status === 'supplied' ? '#4caf50' : '#f44336',
                        fontWeight: 'medium',
                        fontSize: '0.75rem',
                      }}
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>
                )}
              </TableRow>
            ))}
            
            {/* Subtotal Row - Update colspan */}
            {orderDetails.Subtotal !== undefined && orderDetails.Subtotal !== orderDetails.TotalAmount && (
              <TableRow>
                <TableCell 
                  colSpan={storageEnabled ? 5 : 4} 
                  align="right" 
                  sx={{ fontWeight: 'bold' }}
                >
                  סה"כ לפני הנחה:
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  {new Intl.NumberFormat('he-IL', {
                    style: 'currency',
                    currency: 'ILS',
                  }).format(orderDetails.Subtotal || 0)}
                </TableCell>
                {orderItemFields.includes("Status") && <TableCell />}
              </TableRow>
            )}
            
            {/* Discount Row - Update colspan */}
            {(orderDetails.DiscountAmount > 0 || orderDetails.DiscountPercentage > 0) && (
              <TableRow>
                <TableCell
                  colSpan={storageEnabled ? 5 : 4}
                  align="right"
                  sx={{ fontWeight: 'bold' }}
                >
                  הנחה{orderDetails.DiscountPercentage > 0 ? ` (${orderDetails.DiscountPercentage}%)` : ''}:
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                  -{new Intl.NumberFormat('he-IL', {
                    style: 'currency',
                    currency: 'ILS',
                  }).format(orderDetails.DiscountAmount || 0)}
                </TableCell>
                {orderItemFields.includes("Status") && <TableCell />}
              </TableRow>
            )}

            {/* Tax Row - Included */}
            {orderDetails.IsTaxable && orderDetails.IsTaxIncluded && orderDetails.TaxAmount > 0 && (
              <TableRow>
                <TableCell
                  colSpan={storageEnabled ? 5 : 4}
                  align="right"
                >
                  <Typography variant="body2" color="text.secondary">
                    מזה מע"מ ({orderDetails.TaxRate || 18}%):
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color="text.secondary">
                    {new Intl.NumberFormat('he-IL', {
                      style: 'currency',
                      currency: 'ILS',
                    }).format(orderDetails.TaxAmount || 0)}
                  </Typography>
                </TableCell>
                {orderItemFields.includes("Status") && <TableCell />}
              </TableRow>
            )}

            {/* Tax Row - Excluded */}
            {orderDetails.IsTaxable && !orderDetails.IsTaxIncluded && orderDetails.TaxAmount > 0 && (
              <TableRow>
                <TableCell
                  colSpan={storageEnabled ? 5 : 4}
                  align="right"
                  sx={{ fontWeight: 'bold' }}
                >
                  מע"מ ({orderDetails.TaxRate || 18}%):
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  +{new Intl.NumberFormat('he-IL', {
                    style: 'currency',
                    currency: 'ILS',
                  }).format(orderDetails.TaxAmount || 0)}
                </TableCell>
                {orderItemFields.includes("Status") && <TableCell />}
              </TableRow>
            )}

            {/* Total Row - Update colspan */}
            <TableRow>
              <TableCell 
                colSpan={storageEnabled ? 5 : 4} 
                align="right" 
                sx={{ fontWeight: 'bold' }}
              >
                סה"כ לתשלום:
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                {new Intl.NumberFormat('he-IL', {
                  style: 'currency',
                  currency: 'ILS',
                }).format(orderDetails.TotalAmount || 0)}
              </TableCell>
              {orderItemFields.includes("Status") && <TableCell />}
            </TableRow>

            {/* Profit Summary Row — managers only, gated on CostPrice data */}
            {role === 'manager' && orderDetails.items && orderDetails.items.some(item => item.ProductCostPrice != null) && (() => {
              const itemsWithCost = orderDetails.items.filter(item => item.ProductCostPrice != null);
              const totalCost = itemsWithCost.reduce((sum, item) => sum + (parseFloat(item.ProductCostPrice) * parseFloat(item.Quantity)), 0);
              const totalRevenue = itemsWithCost.reduce((sum, item) => sum + (parseFloat(item.UnitPrice) * parseFloat(item.Quantity)), 0);
              const totalProfit = totalRevenue - totalCost;
              const profitPct = totalCost > 0 ? ((totalProfit / totalCost) * 100) : 0;
              const fmt = (v) => new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(v);
              return (
                <TableRow sx={{ backgroundColor: totalProfit >= 0 ? '#f1f8e9' : '#fce4ec' }}>
                  <TableCell
                    colSpan={storageEnabled ? 5 : 4}
                    align="right"
                    sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}
                  >
                    רווח: {fmt(totalProfit)} ({profitPct.toFixed(1)}%)
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.85rem' }}>
                    עלות: {fmt(totalCost)}
                  </TableCell>
                  {orderItemFields.includes("Status") && <TableCell />}
                </TableRow>
              );
            })()}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Order Notes */}
      {orderDetails.Notes && (
        <Paper sx={{ mt: 2, p: 2, boxShadow: 3, borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>הערות</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{orderDetails.Notes}</Typography>
        </Paper>
      )}
    </Box>
  );
}

// ==========================
// NEW: Transaction Detail Panel Component
// ==========================
function TransactionDetailPanel({ transaction, transactionFields, transactionFieldsSchema, debtFieldsSchema, clientFieldsSchema, orderTransactionFieldsSchema, onEdit, onDelete, userRole }) {
  if (!transaction) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        אין מידע נוסף להצגה
      </Alert>
    );
  }

  // Helper function to get field label
  const getFieldLabel = (fieldName) => {
    return transactionFieldsSchema[fieldName]?.label || 
           debtFieldsSchema[fieldName]?.label || 
           clientFieldsSchema[fieldName]?.label ||
           orderTransactionFieldsSchema[fieldName]?.label ||
           fieldName;
  };

  // Helper function to format field value
  const formatFieldValue = (fieldName, value) => {
    if (value === null || value === undefined || value === '') {
      return '–';
    }

    // Handle objects (like payment_details) - MUST BE BEFORE toString()
    if (typeof value === 'object' && value !== null) {
      // Special handling for payment_details
      if (fieldName === 'payment_details') {
        const parts = [];
        if (value.check_number) parts.push(`צ'ק מס' ${value.check_number}`);
        if (value.check_date) parts.push(`תאריך: ${value.check_date}`);
        if (value.check_bank) parts.push(`בנק: ${value.check_bank}`);
        if (value.card_last_four) parts.push(`כרטיס: ****${value.card_last_four}`);
        if (value.card_type) parts.push(`סוג: ${value.card_type}`);
        if (value.bank_name) parts.push(`בנק: ${value.bank_name}`);
        if (value.account_last4) parts.push(`חשבון: ****${value.account_last4}`);
        if (value.transfer_date) parts.push(`תאריך העברה: ${value.transfer_date}`);
        return parts.length > 0 ? parts.join(', ') : JSON.stringify(value, null, 2);
      }
      // For other objects, stringify them
      return JSON.stringify(value, null, 2);
    }

    // Special formatting for different field types
    if (fieldName === 'Type') {
      if (value === 'Payment') return 'תשלום';
      if (value === 'Charge') return 'חיוב';
      if (value === 'Credit') return 'זיכוי';
      return value;
    }

    if (fieldName === 'SubType') {
      // Use the debt field schema labels if available
      if (debtFieldsSchema[value]) {
        return debtFieldsSchema[value].label;
      }
      if (clientFieldsSchema[value]) {
        return clientFieldsSchema[value].label;
      }
      if (orderTransactionFieldsSchema[value]) {
        return orderTransactionFieldsSchema[value].label;
      }
      return value;
    }

    if (fieldName === 'Timestamp' || fieldName.includes('Date') || fieldName.includes('_at')) {
      const date = new Date(value);
      if (fieldName === 'Timestamp') {
        // Full date and time for timestamp
        return date.toLocaleString('he-IL', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } else {
        // Just date for other date fields
        return date.toLocaleDateString('he-IL');
      }
    }

    if (fieldName === 'Amount' || fieldName === 'TotalAfterTR' || fieldName.includes('amount') || fieldName.includes('total') || fieldName === 'Price') {
      const amount = parseFloat(value) || 0;
      const formattedAmount = new Intl.NumberFormat('he-IL', {
        style: 'currency',
        currency: 'ILS',
      }).format(amount);
      
      // Special handling for Amount field to show direction
      if (fieldName === 'Amount' && transaction.Type) {
        const type = transaction.Type;
        if (type === 'Payment' || type === 'Credit') {
          return `${formattedAmount}-`;
        } else if (type === 'Charge') {
          return `${formattedAmount}+`;
        }
      }
      
      return formattedAmount;
    }

    if (fieldName === 'CheckNumber' || fieldName === 'check_number') {
      return `צ'ק מס' ${value}`;
    }

    if (fieldName === 'PaymentMethod' || fieldName === 'payment_method') {
      const methods = {
        'cash': 'מזומן',
        'credit_card': 'כרטיס אשראי',
        'bank_transfer': 'העברה בנקאית',
        'check': 'צ\'ק',
        'other': 'אחר'
      };
      return methods[value] || value;
    }

    if (typeof value === 'boolean') {
      return value ? 'כן' : 'לא';
    }

    return value.toString();
  };

  // Get transaction type color for text only
  const getTypeColor = (type) => {
    switch (type) {
      case 'Payment':
        return '#2e7d32';
      case 'Charge':
        return '#c62828';
      case 'Credit':
        return '#f57f17';
      default:
        return '#757575';
    }
  };

  // Helper function to format payment type
  const formatPaymentType = (type) => {
    const types = {
      'cash': 'מזומן',
      'credit_card': 'כרטיס אשראי',
      'bank_transfer': 'העברה בנקאית',
      'check': 'צ\'ק',
      'other': 'אחר'
    };
    return types[type] || type;
  };

  // Helper function to format check status
  const formatCheckStatus = (status) => {
    const statuses = {
      'pending': 'ממתין',
      'cleared': 'נפרע',
      'bounced': 'חזר',
      'cancelled': 'בוטל'
    };
    return statuses[status] || status;
  };

  // Helper function to get check status chip color
  const getCheckStatusColor = (status) => {
    switch (status) {
      case 'cleared': return 'success';
      case 'bounced': return 'error';
      case 'cancelled': return 'default';
      default: return 'warning';  // pending
    }
  };

  const typeColor = getTypeColor(transaction.Type);

  // Fields to exclude from display (internal fields and date fields shown separately)
  const excludeFields = ['TrID', 'Client', 'ClientName', 'ClientBusinessID', 'reason', 'payment_details', 'DiscountReason', 'DiscountNote', 'Comment', 'Comments', 'Timestamp', 'ActualDate'];
  
  // Text/note fields to display separately
  const noteFields = ['Note', 'Notes'];
  
  // Main fields to display (from transactionFields config, excluding internal ones)
  const mainFields = transactionFields.filter(field => 
    !excludeFields.includes(field) && 
    !noteFields.includes(field) &&
    transaction[field] !== undefined &&
    transaction[field] !== null &&
    transaction[field] !== ''
  );

  // Get all note fields from transaction fields that should be shown
  const configuredNoteFields = transactionFields.filter(field => 
    noteFields.includes(field)
  );
  
  // Also check for note fields that exist in the transaction but not in config
  const extraNoteFields = noteFields.filter(field =>
    !configuredNoteFields.includes(field) &&
    transaction[field] !== undefined
  );
  
  // Combine all note fields to display
  const allNoteFields = [...configuredNoteFields, ...extraNoteFields];

  // Check if user can edit (only for non-order related transactions)
  const canEdit = userRole === 'manager' && 
                  transaction.SubType !== 'OrderPayment' && 
                  transaction.SubType !== 'Order' &&
                  transaction.SubType !== 'OrderCancellation' &&
                  transaction.SubType !== 'PaymentReversal' &&
                  transaction.SubType !== 'PaymentAdjustment' &&
                  transaction.SubType !== 'OrderAdjustment';

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, backgroundColor: '#fafafa' }}>
      {/* Transaction Type Header with Edit Button */}
      <Box sx={{ mb: { xs: 2, sm: 3 }, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: { xs: 1, sm: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: typeColor, fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
            {formatFieldValue('Type', transaction.Type)}
            {transaction.SubType && (
              <Typography component="span" sx={{ fontWeight: 'normal', fontSize: { xs: '0.85rem', sm: '1.1rem' }, color: 'text.secondary', ml: 1 }}>
                {' - '}{formatFieldValue('SubType', transaction.SubType)}
              </Typography>
            )}
          </Typography>
          {transaction.IsEdited && (
            <Chip label="נערך" color="warning" size="small" />
          )}
        </Box>

        {canEdit && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              onClick={() => onEdit(transaction)}
              sx={{
                fontWeight: 'bold',
                flex: { xs: 1, sm: 'none' },
                backgroundColor: '#1976d2',
                '&:hover': { backgroundColor: '#1565c0' },
              }}
            >
              ערוך תנועה
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={() => onDelete(transaction)}
              sx={{ fontWeight: 'bold', flex: { xs: 1, sm: 'none' } }}
            >
              מחק תנועה
            </Button>
          </Box>
        )}
      </Box>

      {/* Main Information Grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(auto-fit, minmax(300px, 1fr))' }, gap: { xs: 1, sm: 2 }, mb: 3 }}>
        {mainFields.map((fieldName) => (
          <Card key={fieldName} sx={{ boxShadow: 1 }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                {getFieldLabel(fieldName)}
              </Typography>
              <Typography sx={{ fontWeight: 'medium', fontSize: { xs: '0.95rem', sm: '1.25rem' } }}>
                {formatFieldValue(fieldName, transaction[fieldName])}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Dates Section */}
      <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
        <Divider sx={{ flexGrow: 1 }} />
        <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
          תאריכים
        </Typography>
        <Divider sx={{ flexGrow: 1 }} />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(auto-fit, minmax(200px, 1fr))' }, gap: { xs: 1, sm: 2 }, mb: 3 }}>
        <Card sx={{ boxShadow: 1 }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              תאריך בפועל
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
              {transaction.ActualDate ? new Date(transaction.ActualDate).toLocaleDateString('he-IL') : '–'}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ boxShadow: 1 }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              תאריך רישום
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
              {transaction.Timestamp ? new Date(transaction.Timestamp).toLocaleString('he-IL', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              }) : '–'}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Payment Methods Section - Only for Payment transactions */}
      {transaction.Type === 'Payment' && transaction.payment_methods?.length > 0 && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
            <Divider sx={{ flexGrow: 1 }} />
            <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
              אמצעי תשלום
            </Typography>
            <Divider sx={{ flexGrow: 1 }} />
          </Box>

          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>סוג</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>סכום</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>פרטים</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transaction.payment_methods.map((pm, index) => (
                  <TableRow key={pm.PaymentMethodID || index}>
                    <TableCell align="center">{formatPaymentType(pm.Type)}</TableCell>
                    <TableCell align="center">
                      {new Intl.NumberFormat('he-IL', {
                        style: 'currency', currency: 'ILS'
                      }).format(pm.Amount)}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        {pm.Type === 'check' && pm.check_details && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
                            <Typography variant="body2">צ'ק #{pm.check_details.CheckNumber}</Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center' }}>
                              {pm.check_details.BankName && (
                                <Typography variant="caption" color="text.secondary">
                                  בנק: {pm.check_details.BankName}
                                </Typography>
                              )}
                              {pm.check_details.AccountNumber && (
                                <Typography variant="caption" color="text.secondary">
                                  | חשבון: {pm.check_details.AccountNumber}
                                </Typography>
                              )}
                              {pm.check_details.DueDate && (
                                <Typography variant="caption" color="text.secondary">
                                  | לפירעון: {new Date(pm.check_details.DueDate).toLocaleDateString('he-IL')}
                                </Typography>
                              )}
                              <Chip
                                label={formatCheckStatus(pm.check_details.Status)}
                                size="small"
                                color={getCheckStatusColor(pm.check_details.Status)}
                                sx={{ ml: 1 }}
                              />
                            </Box>
                          </Box>
                        )}
                        {pm.Type === 'credit_card' && (
                          <Typography variant="body2">
                            {pm.CardType && `${pm.CardType} `}
                            {pm.CardLastFour && `****${pm.CardLastFour}`}
                          </Typography>
                        )}
                        {pm.Type === 'bank_transfer' && (
                          <Typography variant="body2">
                            {pm.BankName}
                            {pm.BankAccountLast4 && ` ****${pm.BankAccountLast4}`}
                            {pm.TransferDate && ` (${new Date(pm.TransferDate).toLocaleDateString('he-IL')})`}
                          </Typography>
                        )}
                        {pm.Type === 'cash' && (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                        {pm.Type === 'other' && pm.Notes && (
                          <Typography variant="body2">{pm.Notes}</Typography>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Notes Section - Always Show */}
      <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
        <Divider sx={{ flexGrow: 1 }} />
        <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
          הערות ופרטים
        </Typography>
        <Divider sx={{ flexGrow: 1 }} />
      </Box>

      {allNoteFields.length > 0 ? (
        allNoteFields.map((fieldName) => {
          const hasContent = transaction[fieldName] && transaction[fieldName].toString().trim() !== '';
          return (
            <Card key={fieldName} sx={{ mb: 2, boxShadow: 1, backgroundColor: '#fffef0' }}>
              <CardHeader 
                title={
                  <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                    {getFieldLabel(fieldName)}
                  </Typography>
                }
                sx={{ 
                  backgroundColor: '#fff9c4', 
                  py: 1,
                }}
              />
              <CardContent sx={{ backgroundColor: '#fffef0' }}>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontSize: '1rem',
                    lineHeight: 1.8,
                    color: hasContent ? '#424242' : 'text.secondary',
                    fontStyle: hasContent ? 'normal' : 'italic'
                  }}
                >
                  {hasContent ? formatFieldValue(fieldName, transaction[fieldName]) : 'אין תוכן'}
                </Typography>
              </CardContent>
            </Card>
          );
        })
      ) : (
        <Card sx={{ boxShadow: 1 }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              אין שדות הערות מוגדרים
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

const ClientDetailPage = () => {
  const { business_id } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useUser();
  const businessProfile = userProfile?.group?.business_profile || {};
  const [client, setClient] = useState(null);

  // User-specific sessionStorage keys for state persistence (prevents data leakage between users)
  const userId = userProfile?.username || 'unknown';

  // SessionStorage keys for order table state persistence (user-specific + client-specific)
  const ORDER_FILTERS_KEY = `client-${business_id}-order-filters-${userId}`;
  const ORDER_PAGE_SIZE_KEY = `client-${business_id}-order-pagesize-${userId}`;
  const ORDER_PAGE_NUMBER_KEY = `client-${business_id}-order-page-${userId}`;

  // SessionStorage keys for transaction table state persistence (user-specific + client-specific)
  const TRANSACTION_FILTERS_KEY = `client-${business_id}-transaction-filters-${userId}`;
  const TRANSACTION_PAGE_SIZE_KEY = `client-${business_id}-transaction-pagesize-${userId}`;
  const TRANSACTION_PAGE_NUMBER_KEY = `client-${business_id}-transaction-page-${userId}`;
  const TRANSACTION_SORT_KEY = `client-${business_id}-transaction-sort-${userId}`;

  // NEW: Dedicated date cache keys with "new day" validation support
  const ORDER_DATE_CACHE_KEY = `client-${business_id}-order-dates-${userId}`;
  const TRANSACTION_DATE_CACHE_KEY = `client-${business_id}-transaction-dates-${userId}`;

  const [clientNotFound, setClientNotFound] = useState(false);
  const [show404Message, setShow404Message] = useState(false);

  // Transaction history
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);

  // Mobile transaction detail dialog
  const [mobileTransactionDetail, setMobileTransactionDetail] = useState(null);

  // ====================================================
  // 4) Date range: cached with "new day" validation
  // ====================================================
  // Dates are optional - when not set, shows all transactions
  // Restored from cache if same day, otherwise reset to null
  const transactionDateCache = getDateCacheWithValidation(TRANSACTION_DATE_CACHE_KEY);
  const [startDate, setStartDate] = useState(transactionDateCache.startDate);
  const [endDate, setEndDate] = useState(transactionDateCache.endDate);

  // Server-side pagination: total row count from API
  const [transactionRowCount, setTransactionRowCount] = useState(0);

  const [transactionType, setTransactionType] = useState(() => {
    try {
      const saved = sessionStorage.getItem(TRANSACTION_FILTERS_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed.transactionType || 'All';
      }
    } catch (e) {
      console.error('Failed to load transaction filters from sessionStorage', e);
    }
    return 'All';
  });

  // NEW: Order number filter
  const [orderNumberFilter, setOrderNumberFilter] = useState(null);
  const [availableOrderNumbers, setAvailableOrderNumbers] = useState([]);

  // NEW: Debt type filter (only used if user has multiple debt fields)
  const [debtTypeFilter, setDebtTypeFilter] = useState('All');

  // Initialize transaction pagination from sessionStorage
  const [transactionPageSize, setTransactionPageSize] = useState(() => {
    try {
      const saved = sessionStorage.getItem(TRANSACTION_PAGE_SIZE_KEY);
      return saved ? parseInt(saved, 10) : 25;
    } catch (e) {
      return 25;
    }
  });

  const [transactionPageNumber, setTransactionPageNumber] = useState(() => {
    try {
      const saved = sessionStorage.getItem(TRANSACTION_PAGE_NUMBER_KEY);
      return saved ? parseInt(saved, 10) : 0;
    } catch (e) {
      return 0;
    }
  });

  // Initialize transaction sort model from sessionStorage or use default
  const [transactionSortModel, setTransactionSortModel] = useState(() => {
    try {
      // Check sessionStorage for cached sort
      const cached = sessionStorage.getItem(TRANSACTION_SORT_KEY);
      if (cached) {
        return JSON.parse(cached);
      }
      // Default to descending by ActualDate (newest first)
      return [{ field: 'ActualDate', sort: 'desc' }];
    } catch (e) {
      console.error('Failed to load transaction sort from sessionStorage', e);
      return [{ field: 'ActualDate', sort: 'desc' }];
    }
  });

  // =============== NEW: Editing Debt ===============
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [confirmStep, setConfirmStep] = useState(false);

  // Debt fields - Now dynamically created based on config
  const [debtFieldValues, setDebtFieldValues] = useState({});

  // Note for debt update
  const [userNote, setUserNote] = useState('');

  // Error/success messages for debt editing
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // NEW: track updating state & ref for final debt update
  const [isUpdatingDebt, setIsUpdatingDebt] = useState(false);
  const confirmDebtButtonRef = useRef(null);

  // =============== NEW: Editing Client Details ===============
  const [clientEditDialogOpen, setClientEditDialogOpen] = useState(false);
  const [clientConfirmStep, setClientConfirmStep] = useState(false);

  // Client fields - Now dynamically created based on config
  const [clientFieldValues, setClientFieldValues] = useState({});
  const [originalClientFieldValues, setOriginalClientFieldValues] = useState({});

  // Error/success for client details editing
  const [clientErrorMsg, setClientErrorMsg] = useState('');
  const [clientSuccessMsg, setClientSuccessMsg] = useState('');

  // NEW: track updating state & ref for final client update
  const [isUpdatingClient, setIsUpdatingClient] = useState(false);
  const confirmClientButtonRef = useRef(null);

  // =============== NEW: Two-step Delete Client ===============
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteErrorMsg, setDeleteErrorMsg] = useState('');
  const [deleteSuccessMsg, setDeleteSuccessMsg] = useState('');

  // ---- The two-step logic:
  const [deleteStep, setDeleteStep] = useState(1);         // step 1 or 2
  const [deletePassword, setDeletePassword] = useState(''); // user's password
  const [passwordVisible, setPasswordVisible] = useState(false);

  // =============== NEW: Client Comments Section ===============
  const [comments, setComments] = useState('');
  const [editingComments, setEditingComments] = useState(false);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsError, setCommentsError] = useState('');
  const [commentsSuccess, setCommentsSuccess] = useState('');
  // NEW: State to control expanded view for comments
  const [expandedComments, setExpandedComments] = useState(false);
  // Ref for the expanded editor textarea (directly the textarea element)
  const textFieldRef = useRef(null);

  // =============== NEW: For deleted client handling ===============
  const [isClientDeleted, setIsClientDeleted] = useState(false);
  const [deletedAt, setDeletedAt] = useState(null);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [restoreError, setRestoreError] = useState('');
  const [restoreSuccess, setRestoreSuccess] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  // Orders section state
  const [clientOrders, setClientOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);

  // Initialize orderFilters from sessionStorage with "new day" cache validation
  // Dates default to null (show all orders) and are restored from cache only if same day
  const orderDateCache = getDateCacheWithValidation(ORDER_DATE_CACHE_KEY);
  const [orderFilters, setOrderFilters] = useState(() => {
    try {
      const saved = sessionStorage.getItem(ORDER_FILTERS_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          Status: parsed.Status || '',
          startDate: orderDateCache.startDate,
          endDate: orderDateCache.endDate,
        };
      }
    } catch (e) {
      console.error('Failed to load order filters from sessionStorage', e);
    }
    return {
      Status: '',
      startDate: orderDateCache.startDate,
      endDate: orderDateCache.endDate,
    };
  });

  const [orderNumberInput, setOrderNumberInput] = useState(''); // What user types/selects
  const orderNumberInputRef = useRef(''); // Track current input without re-renders
  const [orderIdForFiltering, setOrderIdForFiltering] = useState(''); // The actual OrderID used for filtering
  const [orderAutocompleteKey, setOrderAutocompleteKey] = useState(0);  // Used to force reset Autocomplete

  // Debounced autocomplete search state for orders
  const [debouncedOrderSearch, setDebouncedOrderSearch] = useState('');
  const orderAutocompleteTimerCleanup = useRef(null);

  // Orders pagination state (server-side) - initialize from sessionStorage
  const [orderPaginationModel, setOrderPaginationModel] = useState(() => {
    try {
      const savedPageSize = sessionStorage.getItem(ORDER_PAGE_SIZE_KEY);
      const savedPage = sessionStorage.getItem(ORDER_PAGE_NUMBER_KEY);
      return {
        page: savedPage ? parseInt(savedPage, 10) : 0,
        pageSize: savedPageSize ? parseInt(savedPageSize, 10) : 25
      };
    } catch (e) {
      return { page: 0, pageSize: 25 };
    }
  });

  // Orders sorting state
  const [orderOrdering, setOrderOrdering] = useState('-OrderDate');

  // Orders total rows (from server)
  const [orderTotalRows, setOrderTotalRows] = useState(0);

  // Orders summary (for managers)
  const [orderSummary, setOrderSummary] = useState({ totalOrders: 0, totalAmount: 0 });

  // Ref for scrolling to orders table when pagination changes
  const ordersTableRef = useRef(null);
  // Ref for scrolling to transactions table when pagination changes
  const transactionsTableRef = useRef(null);

  const role = userProfile?.role;
  
  const [exportOrderItemsDialogOpen, setExportOrderItemsDialogOpen] = useState(false);
  const [monthlyStatementDialogOpen, setMonthlyStatementDialogOpen] = useState(false);
  const [ordersMonthlyStatementDialogOpen, setOrdersMonthlyStatementDialogOpen] = useState(false);
  // =============== NEW: Transaction Editing ===============
  const [editTransactionDialogOpen, setEditTransactionDialogOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);

  // =============== NEW: Transaction Deletion ===============
  const [deleteTransactionDialogOpen, setDeleteTransactionDialogOpen] = useState(false);
  const [deletingTransaction, setDeletingTransaction] = useState(null);
  const [transactionDeletePassword, setTransactionDeletePassword] = useState('');
  const [isDeletingTransaction, setIsDeletingTransaction] = useState(false);
  const [transactionDeleteError, setTransactionDeleteError] = useState('');

  // Get UI configuration from user profile
  const uiConfig = userProfile?.ui_config || {};

  // Memoize arrays/objects to prevent infinite loops caused by new references on every render
  const clientFieldsSchema = useMemo(() => uiConfig.client_fields_schema || {}, [uiConfig.client_fields_schema]);
  const debtFieldsSchema = useMemo(() => uiConfig.debt_fields_schema || {}, [uiConfig.debt_fields_schema]);
  const clientFields = useMemo(() => uiConfig.client_fields || [], [uiConfig.client_fields]);
  const debtFields = useMemo(() => uiConfig.debt_fields || [], [uiConfig.debt_fields]);

  const enforceIdValidation = uiConfig.enforce_id !== false;
  const autoGenerateId = uiConfig.auto_generate_id === true;

  // Helper function to calculate age from birthdate
  const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };
  const includeOrders = uiConfig.include_orders || false;
  const allowOrderManagement = uiConfig.allow_order_management === true;
  const useTotalDebt = uiConfig.use_total_debt !== false;

  // Get transaction fields configuration - memoized to prevent infinite loops
  const transactionFields = useMemo(() => uiConfig.transaction_fields || [], [uiConfig.transaction_fields]);
  const transactionFieldsSchema = useMemo(() => uiConfig.transaction_fields_schema || {}, [uiConfig.transaction_fields_schema]);
  const orderTransactionFieldsSchema = useMemo(() => uiConfig.order_transaction_fields_schema || {}, [uiConfig.order_transaction_fields_schema]);
  const orderFields = useMemo(() => uiConfig.order_fields || [], [uiConfig.order_fields]);
  const orderFieldsSchema = useMemo(() => uiConfig.order_fields_schema || {}, [uiConfig.order_fields_schema]);
  const orderItemFields = useMemo(() => uiConfig.order_item_fields || [], [uiConfig.order_item_fields]);
  const orderItemFieldsSchema = useMemo(() => uiConfig.order_item_fields_schema || {}, [uiConfig.order_item_fields_schema]);
  const generalSchema = useMemo(() => uiConfig?.orders_general_configs?.general_schema || {}, [uiConfig?.orders_general_configs?.general_schema]); 

  const productLabel = generalSchema?.product?.label || 'מוצר';
  const productPluralLabel = generalSchema?.product?.plural_label || 'מוצרים';
  const orderLabel = generalSchema?.order?.label || 'הזמנה';
  const orderPluralLabel = generalSchema?.order?.plural_label || 'הזמנות';
  const newOrderLabel = generalSchema?.new_order?.label || 'הזמנה חדשה';
  
  const editClientRoles = uiConfig.pages_configs?.ClientDetailPage?.edit_client_roles || ["manager"];
  const deleteClientRoles = uiConfig.pages_configs?.ClientDetailPage?.delete_client_roles || ["manager"];
  const groupAccessRoles = uiConfig.pages_configs?.ClientDetailPage?.group_acces_roles || ["manager"];
  const restoreClientRoles = uiConfig.pages_configs?.ClientDetailPage?.restore_client_roles || ["manager"];

  // Summary configuration
  const summaryAllowedRoles = uiConfig?.pages_configs?.TransactionsPage?.summary_roles || ['manager'];  
  const [summaryData, setSummaryData] = useState({
    payment_total: 0,
    charge_total: 0,
    credit_total: 0,
  });
  const queryClient = useQueryClient();

  // NEW: Auto-clear comments success message after 3 seconds
  useEffect(() => {
    if (commentsSuccess) {
      const timer = setTimeout(() => setCommentsSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [commentsSuccess]);

  // ======================================
  // Fetch client details
  // ======================================
  useEffect(() => {
    const fetchClient = async () => {
      try {
        const response = await API.get(`/client/?business_id=${business_id}&include_deleted=true`);
        // If we reach here, client is not deleted (status 200)
        setIsClientDeleted(false);
        setDeletedAt(null);
        setClient(response.data);
      } catch (error) {
        console.error('Error fetching client details:', error);
        
        // Check specifically for 410 status code (Gone - deleted client)
        if (error.response && error.response.status === 410) {
          const responseData = error.response.data;
          setIsClientDeleted(true);
          setDeletedAt(responseData.DeletedAt);
          setClient(responseData.client);
        } else {
          // Any other error - client not found or server error
          setClientNotFound(true);
          setShow404Message(true);
          // Hide message after 2 seconds and redirect
          const timer = setTimeout(() => {
            navigate('/clients');
          }, 2000);
          
          // Cleanup timeout if component unmounts
          return () => clearTimeout(timer);
        }
      }
    };
    fetchClient();
  }, [business_id, refreshKey]);

  // Update comments state when client data is loaded
  useEffect(() => {
    if (client) {
      setComments(client.ClientComments || '');
      
      // Initialize debt field values from client data
      if (client.debt && client.debt.DebtFields) {
        const initialDebtValues = {};
        debtFields.forEach(field => {
          if (field !== 'TotalDebt') { // TotalDebt is handled differently
            initialDebtValues[field] = client.debt.DebtFields[field] || 0;
          }
        });
        setDebtFieldValues(initialDebtValues);
      }
      
      // Initialize client field values
      const initialClientValues = {};
      const originalValues = {};
      clientFields.forEach(field => {
        initialClientValues[field] = client[field] || '';
        originalValues[field] = client[field] || '';
      });
      setClientFieldValues(initialClientValues);
      setOriginalClientFieldValues(originalValues);
    }
  }, [client, debtFields, clientFields]);

  // ======================================
  // Fetch transaction summary (memoized)
  // ======================================
  const fetchTransactionSummary = useCallback(async () => {
    if (!summaryAllowedRoles.includes(role)) {
      return; // Don't fetch if user doesn't have permission
    }

    try {
      const params = {
        business_id: business_id, // Always filter by current client
      };

      // Add transaction type filter
      if (transactionType !== 'All') {
        params.transaction_type = transactionType;
      }

      // Add date filters
      if (startDate instanceof Date && !isNaN(startDate)) {
        params.start_date = format(startDate, 'yyyy-MM-dd');
      }
      if (endDate instanceof Date && !isNaN(endDate)) {
        params.end_date = format(endDate, 'yyyy-MM-dd');
      }

      const response = await API.get('/transactions/summary/', { params });
      setSummaryData(response.data);
    } catch (error) {
      console.error('Error fetching transaction summary:', error);
    }
  }, [business_id, summaryAllowedRoles, role, transactionType, startDate, endDate]);

// Load client orders (reactive to filter changes)
  useEffect(() => {
    if (client && includeOrders && !isClientDeleted) {
      fetchClientOrders();

      // Only fetch summary if date range is set
      const hasDateRange = (orderFilters.startDate instanceof Date && !isNaN(orderFilters.startDate)) ||
                           (orderFilters.endDate instanceof Date && !isNaN(orderFilters.endDate));
      if (hasDateRange) {
        fetchOrderSummary();
      } else {
        // Clear summary when no date range
        setOrderSummary({ totalOrders: 0, totalAmount: 0 });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business_id, includeOrders, client, isClientDeleted, orderFilters.Status, orderFilters.startDate, orderFilters.endDate, orderIdForFiltering]);

  // Refetch orders when pagination or ordering changes
  useEffect(() => {
    if (client && includeOrders && !isClientDeleted) {
      fetchClientOrders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderPaginationModel.page, orderPaginationModel.pageSize, orderOrdering]);

  // Persist orderPaginationModel to sessionStorage
  useEffect(() => {
    try {
      sessionStorage.setItem(ORDER_PAGE_SIZE_KEY, orderPaginationModel.pageSize.toString());
      sessionStorage.setItem(ORDER_PAGE_NUMBER_KEY, orderPaginationModel.page.toString());
    } catch (e) {
      console.error('Failed to save order pagination to sessionStorage', e);
    }
  }, [orderPaginationModel, ORDER_PAGE_SIZE_KEY, ORDER_PAGE_NUMBER_KEY]);

  // Persist transaction filters to sessionStorage (excluding dates to avoid stale date ranges)
  useEffect(() => {
    try {
      const filters = {
        transactionType,
      };
      sessionStorage.setItem(TRANSACTION_FILTERS_KEY, JSON.stringify(filters));
    } catch (e) {
      console.error('Failed to save transaction filters to sessionStorage', e);
    }
  }, [transactionType, TRANSACTION_FILTERS_KEY]);

  // Persist transaction page size to sessionStorage
  useEffect(() => {
    try {
      sessionStorage.setItem(TRANSACTION_PAGE_SIZE_KEY, transactionPageSize.toString());
    } catch (e) {
      console.error('Failed to save transaction page size to sessionStorage', e);
    }
  }, [transactionPageSize, TRANSACTION_PAGE_SIZE_KEY]);

  // Persist transaction page number to sessionStorage
  useEffect(() => {
    try {
      sessionStorage.setItem(TRANSACTION_PAGE_NUMBER_KEY, transactionPageNumber.toString());
    } catch (e) {
      console.error('Failed to save transaction page number to sessionStorage', e);
    }
  }, [transactionPageNumber, TRANSACTION_PAGE_NUMBER_KEY]);

  // Persist transaction sort model to sessionStorage (clears on tab close)
  useEffect(() => {
    try {
      sessionStorage.setItem(TRANSACTION_SORT_KEY, JSON.stringify(transactionSortModel));
    } catch (e) {
      console.error('Failed to save transaction sort to sessionStorage', e);
    }
  }, [transactionSortModel, TRANSACTION_SORT_KEY]);

  // Persist order date range to cache with "cachedOn" date for "new day" validation
  useEffect(() => {
    saveDateCache(ORDER_DATE_CACHE_KEY, orderFilters.startDate, orderFilters.endDate);
  }, [orderFilters.startDate, orderFilters.endDate, ORDER_DATE_CACHE_KEY]);

  // Persist transaction date range to cache with "cachedOn" date for "new day" validation
  useEffect(() => {
    saveDateCache(TRANSACTION_DATE_CACHE_KEY, startDate, endDate);
  }, [startDate, endDate, TRANSACTION_DATE_CACHE_KEY]);

  // Apply config sort order if no cached value exists (on mount only)
  useEffect(() => {
    try {
      const cached = sessionStorage.getItem(TRANSACTION_SORT_KEY);
      if (!cached && uiConfig?.pages_configs?.ClientDetailPage?.transaction_print_sort_order) {
        const configSort = uiConfig.pages_configs.ClientDetailPage.transaction_print_sort_order;
        setTransactionSortModel([{ field: 'ActualDate', sort: configSort }]);
      }
    } catch (e) {
      console.error('Failed to apply config sort order', e);
    }
  }, [uiConfig, TRANSACTION_SORT_KEY]); // Only run when uiConfig is available

  // Note: orderSummary is now a state variable (line 856) that gets updated via fetchOrderSummary
  // This ensures accurate totals for ALL filtered orders, not just the current page

  // ======================================
  // Filter logic
  // ======================================
  // Server-side filtering - no client-side filteredTransactions needed
  // ======================================

  // Reset transaction page to 0 when filters change (prevent invalid page error)
  useEffect(() => {
    setTransactionPageNumber(0);
  }, [transactionType, startDate, endDate, orderNumberFilter]);

  // State for order summary fetched from backend
  const [orderSummaryData, setOrderSummaryData] = useState(null);

  // =============== Fetch order numbers (runs once on mount for autocomplete) ===============
  const fetchOrderNumbers = useCallback(async () => {
    try {
      const response = await API.get(`/client/transactions/?business_id=${business_id}`);
      if (response.data && response.data.length > 0) {
        const hasOrderNumberField = transactionFields.includes('OrderNumber');
        if (hasOrderNumberField) {
          const uniqueOrderNumbers = [...new Set(
            response.data
              .map(tx => tx.OrderNumber)
              .filter(orderNum => orderNum && orderNum !== '' && orderNum !== null && orderNum !== undefined)
          )].sort();
          setAvailableOrderNumbers(uniqueOrderNumbers);
        }
      }
    } catch (error) {
      console.error('Error fetching order numbers:', error);
    }
  }, [business_id, transactionFields]);

  // =============== Fetch paginated transactions (main table data) ===============
  const fetchTransactions = useCallback(async () => {
    setLoadingTransactions(true);
    try {
      const params = {
        business_id: business_id,
        page: transactionPageNumber + 1, // Convert 0-based to 1-based for DRF
        page_size: transactionPageSize,
      };

      if (transactionType !== 'All') {
        params.transaction_type = transactionType;
      }

      if (startDate instanceof Date && !isNaN(startDate)) {
        params.start_date = format(startDate, 'yyyy-MM-dd');
      }

      if (endDate instanceof Date && !isNaN(endDate)) {
        params.end_date = format(endDate, 'yyyy-MM-dd');
      }

      // Order number filter (server-side)
      if (orderNumberFilter) {
        params.order_number = orderNumberFilter;
      }

      // Debt type filter (server-side)
      if (debtTypeFilter !== 'All') {
        params.subtype = debtTypeFilter;
      }

      // Ordering based on sort model
      if (transactionSortModel.length > 0) {
        const sortField = transactionSortModel[0].field;
        const sortDir = transactionSortModel[0].sort === 'asc' ? '' : '-';
        params.ordering = `${sortDir}${sortField}`;
      }

      const response = await API.get('/transactions/', { params });
      setTransactions(response.data.results);
      setTransactionRowCount(response.data.count);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoadingTransactions(false);
    }
  }, [business_id, transactionPageNumber, transactionPageSize, transactionType, startDate, endDate, orderNumberFilter, debtTypeFilter, transactionSortModel]);

  // Fetch transactions when pagination or filters change
  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // Fetch order numbers once on mount
  useEffect(() => {
    fetchOrderNumbers();
  }, [fetchOrderNumbers]);

  // =============== Fetch transaction summary by order number from backend ===============
  const fetchTransactionOrderSummary = useCallback(async () => {
    if (!orderNumberFilter) {
      setOrderSummaryData(null);
      return;
    }

    try {
      const params = {
        business_id: business_id,
        order_number: orderNumberFilter,
      };
      const response = await API.get('/transactions/summary/', { params });
      setOrderSummaryData({
        orderNumber: orderNumberFilter,
        payment_total: response.data.payment_total || 0,
        charge_total: response.data.charge_total || 0,
        credit_total: response.data.credit_total || 0,
      });
    } catch (error) {
      console.error('Error fetching transaction order summary:', error);
      setOrderSummaryData(null);
    }
  }, [business_id, orderNumberFilter]);

  useEffect(() => {
    fetchTransactionOrderSummary();
  }, [fetchTransactionOrderSummary]);

  // ======================================
  // Refresh transactions function (triggers re-fetch of both transactions and order numbers)
  // ======================================
  const refreshTransactions = useCallback(async () => {
    // Re-fetch order numbers for autocomplete
    await fetchOrderNumbers();
    // Re-fetch paginated transactions
    await fetchTransactions();
    // Re-fetch summary if date range is set
    const hasDateRange = (startDate instanceof Date && !isNaN(startDate)) ||
                         (endDate instanceof Date && !isNaN(endDate));
    if (hasDateRange) {
      fetchTransactionSummary();
    }
    // Re-fetch transaction order summary if order number is selected
    if (orderNumberFilter) {
      fetchTransactionOrderSummary();
    }
  }, [fetchOrderNumbers, fetchTransactions, fetchTransactionSummary, fetchTransactionOrderSummary, startDate, endDate, orderNumberFilter]);

// =============== Update date summary when filters change (only when date range is set) ===============
useEffect(() => {
  const hasDateRange = (startDate instanceof Date && !isNaN(startDate)) ||
                       (endDate instanceof Date && !isNaN(endDate));
  if (hasDateRange) {
    fetchTransactionSummary();
  } else {
    // Clear summary when no date range
    setSummaryData({ payment_total: 0, charge_total: 0, credit_total: 0 });
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [transactionType, startDate, endDate, role]); // fetchTransactionSummary excluded to prevent infinite loop

// ======================================
// Dynamic Transaction Columns
// ======================================
const transactionColumns = useMemo(() => {
  const columnsArray = [];

  // Get configured fields and ensure ActualDate is included (replacing Timestamp in the grid)
  const configuredFields = uiConfig.transaction_fields || [];
  const fieldsToShow = [...configuredFields];

  // Ensure ActualDate is in the list (it may not be in config yet)
  if (!fieldsToShow.includes('ActualDate')) {
    // Insert ActualDate where Timestamp would be, or at end
    const timestampIndex = fieldsToShow.indexOf('Timestamp');
    if (timestampIndex !== -1) {
      fieldsToShow.splice(timestampIndex, 0, 'ActualDate');
    } else {
      fieldsToShow.push('ActualDate');
    }
  }

  // Generate columns dynamically from transaction fields configuration
  for (const fieldName of fieldsToShow) {
    // Skip TrID as it's used for row ID, skip client references too
    // Skip Timestamp - we use ActualDate for the datagrid (Timestamp shown in detail panel only)
    if (fieldName === 'TrID' || fieldName === 'ClientName' || fieldName === 'ClientBusinessID' || fieldName === 'Timestamp') {
      continue;
    }

    const fieldConfig = transactionFieldsSchema[fieldName] || {};
    // Use Hebrew labels for specific fields
    let label = fieldConfig.label || fieldName;
    if (fieldName === 'ActualDate') {
      label = 'תאריך בפועל';
    } else if (fieldName === 'Timestamp') {
      label = 'תאריך רישום';
    } else if (fieldName === 'IsEdited') {
      label = 'נערך';
    }

    // Hide non-essential columns on mobile (keep ActualDate, Type, Amount)
    const mobileHiddenFields = ['Timestamp', 'SubType', 'IsEdited', 'Description', 'Note'];
    let column = {
      field: fieldName,
      headerName: label,
      flex: 1,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      ...(mobileHiddenFields.includes(fieldName) && { mobile: false }),
    };

    // Apply appropriate renderers based on field type
    if (fieldName === 'ActualDate') {
      // ActualDate shows just the date (no time)
      column.minWidth = 120;
      column.renderCell = (params) => {
        if (!params.value) return '';
        const date = new Date(params.value);
        const displayDate = `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()}`;
        return (
          <Tooltip title={displayDate} componentsProps={{ tooltip: { sx: { fontSize: '1.15rem', padding: '10px 16px', backgroundColor: '#fff', color: '#333', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', border: '1px solid #e0e0e0' } } }}>
            <span style={{ whiteSpace: 'nowrap', fontSize: '0.95rem', direction: 'ltr', display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
              {displayDate}
            </span>
          </Tooltip>
        );
      };
    }
    else if (fieldName === 'Timestamp' || fieldConfig.type === 'date') {
      // Timestamp and other date fields show date and time
      column.minWidth = 170;
      column.renderCell = (params) => {
        if (!params.value) return '';
        const date = new Date(params.value);
        const displayDate = `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
        return (
          <Tooltip title={displayDate} componentsProps={{ tooltip: { sx: { fontSize: '1.15rem', padding: '10px 16px', backgroundColor: '#fff', color: '#333', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', border: '1px solid #e0e0e0' } } }}>
            <span style={{ whiteSpace: 'nowrap', fontSize: '0.95rem', direction: 'ltr', display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
              {displayDate}
            </span>
          </Tooltip>
        );
      };
    }
    else if (fieldName === 'IsEdited') {
      // Show edited indicator as a chip
      column.minWidth = 80;
      column.renderCell = (params) => {
        if (!params.value) return '';
        return (
          <Chip
            label="נערך"
            color="warning"
            size="small"
            sx={{ fontWeight: 'bold' }}
          />
        );
      };
    }
    else if (fieldName === 'Type') {
      column.renderCell = (params) => {
        const t = params.value;
        let displayValue = t || '';
        if (t === 'Payment') displayValue = 'תשלום';
        if (t === 'Charge') displayValue = 'חיוב';
        if (t === 'Credit') displayValue = 'זיכוי';
        return (
          <Tooltip title={displayValue} componentsProps={{ tooltip: { sx: { fontSize: '1.15rem', padding: '10px 16px', backgroundColor: '#fff', color: '#333', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', border: '1px solid #e0e0e0' } } }}>
            <span style={{ whiteSpace: 'nowrap', fontSize: '0.95rem' }}>
              {displayValue}
            </span>
          </Tooltip>
        );
      };
    }
    else if (fieldName === 'SubType') {
      column.renderCell = (params) => {
        let subtype = params.value || '';
        // Use the debt field schema labels if available
        if (debtFieldsSchema[subtype]) {
          subtype = debtFieldsSchema[subtype].label;
        }
        if (clientFieldsSchema[subtype]) {
          subtype = clientFieldsSchema[subtype].label;
        }
        if (orderTransactionFieldsSchema[subtype]) {
          subtype = orderTransactionFieldsSchema[subtype].label
        }
        return (
          <Tooltip 
            title={subtype} 
            arrow 
            placement="top"
            enterDelay={300}
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: 'white',
                  color: 'black',
                  fontSize: '1.2rem',
                  maxWidth: 500,
                  padding: '10px 12px',
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap'
                }
              },
              arrow: {
                sx: {
                  color: 'white',
                  '&::before': {
                    border: '1px solid rgba(0, 0, 0, 0.1)'
                  }
                }
              }
            }}
          >
            <div style={{ 
              overflow: 'hidden', 
              textOverflow: 'ellipsis', 
              whiteSpace: 'nowrap',
              width: '100%',
              cursor: 'pointer',
              textAlign: 'center'
            }}>
              {subtype}
            </div>
          </Tooltip>
        );
      };
    } 
    else if (fieldName === 'Amount' || fieldName === 'TotalAfterTR' || fieldConfig.type === 'number') {
      // Common handler for numeric fields
      column.valueGetter = (value, row) => {
        const v = row[fieldName];
        if (!v) return 0;
        return parseFloat(v) || 0;
      };

      column.renderCell = (params) => {
        const value = parseFloat(params.value || 0);
        const formattedValue = new Intl.NumberFormat('he-IL', {
          style: 'currency',
          currency: 'ILS',
        }).format(value);

        // Special display for Amount based on transaction type
        if (fieldName === 'Amount') {
          const type = params.row?.Type || '';
          let displayValue = formattedValue;
          switch (type) {
            case 'Payment':
            case 'Credit':
              displayValue = `${formattedValue}-`;
              break;
            case 'Charge':
              displayValue = `${formattedValue}+`;
              break;
          }
          return (
            <Tooltip title={displayValue} componentsProps={{ tooltip: { sx: { fontSize: '1.15rem', padding: '10px 16px', backgroundColor: '#fff', color: '#333', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', border: '1px solid #e0e0e0' } } }}>
              <span style={{ whiteSpace: 'nowrap', fontSize: '0.95rem' }}>
                {displayValue}
              </span>
            </Tooltip>
          );
        }

        return (
          <Tooltip title={formattedValue} componentsProps={{ tooltip: { sx: { fontSize: '1.15rem', padding: '10px 16px', backgroundColor: '#fff', color: '#333', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', border: '1px solid #e0e0e0' } } }}>
            <span style={{ whiteSpace: 'nowrap', fontSize: '0.95rem' }}>
              {formattedValue}
            </span>
          </Tooltip>
        );
      };
    } 
     
    else if (fieldName === 'Note') {
      column.minWidth = 150; // Give more width to notes
      column.flex = 1.5;    // Let it take more space proportionally
      
      // Special rendering for notes with formatted content
      column.renderCell = (params) => {
        const value = params.value || '';
        const hasOrderTable = value.includes('================');
        
        if (hasOrderTable) {
          // For notes with order tables, show a preview with an icon
          const firstLine = value.split('\n')[0];
          return (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              width: '100%',
              cursor: 'pointer'
            }}>
              <Typography
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1
                }}
              >
                {firstLine}
              </Typography>
            </Box>
          );
        }
        
        // Regular notes without tables
        return (
          <Tooltip 
            title={value} 
            arrow 
            placement="top"
            enterDelay={300}
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: 'white',
                  color: 'black',
                  fontSize: '1.2rem',
                  maxWidth: 600,
                  padding: '10px 12px',
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'monospace' // For better table alignment
                }
              },
              arrow: {
                sx: {
                  color: 'white',
                  '&::before': {
                    border: '1px solid rgba(0, 0, 0, 0.1)'
                  }
                }
              }
            }}
          >
            <div style={{ 
              overflow: 'hidden', 
              textOverflow: 'ellipsis', 
              whiteSpace: 'nowrap',
              width: '100%',
              cursor: 'pointer',
              textAlign: 'center'
            }}>
              {value}
            </div>
          </Tooltip>
        );
      };
    } 
    else if (fieldConfig.type === 'string' || fieldName === 'Invoice' ) {
      // For Note field specifically, give it more width and better display
      if (fieldName === 'Note') {
        column.minWidth = 150; // Give more width to notes
        column.flex = 1.5;    // Let it take more space proportionally
      }
      
      // String fields with tooltip
      column.renderCell = (params) => {
        const value = params.value || '';
        return (
          <Tooltip 
            title={value} 
            arrow 
            placement="top"
            enterDelay={300}
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: 'white',
                  color: 'black',
                  fontSize: '1.2rem',
                  maxWidth: 500,
                  padding: '10px 12px',
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap'
                }
              },
              arrow: {
                sx: {
                  color: 'white',
                  '&::before': {
                    border: '1px solid rgba(0, 0, 0, 0.1)'
                  }
                }
              }
            }}
          >
            <div style={{ 
              overflow: 'hidden', 
              textOverflow: 'ellipsis', 
              whiteSpace: 'nowrap',
              width: '100%',
              cursor: 'pointer',
              textAlign: 'center'
            }}>
              {value}
            </div>
          </Tooltip>
        );
      };
    }
    
    // Handle extra fields dynamically (fields not in standard transaction model)
    const standardFields = ['ClientName', 'ClientBusinessID', 'Type', 'SubType', 'Invoice', 'Amount', 'TotalAfterTR', 'Timestamp', 'ActualDate', 'Note', 'IsEdited'];
    if (!standardFields.includes(fieldName)) {
      column.valueGetter = (value, row) => {
        return row?.[fieldName] || '';
      };
    }
    
    columnsArray.push(column);
  }
  
  return columnsArray;
}, [transactionFieldsSchema, debtFieldsSchema, clientFieldsSchema, uiConfig.transaction_fields]);

// Quick-actions 3-dot menu state for orders
const [orderActionMenuAnchor, setOrderActionMenuAnchor] = useState(null);
const [orderActionMenuRow, setOrderActionMenuRow] = useState(null);
const [orderStatusDialogOpen, setOrderStatusDialogOpen] = useState(false);

const handleOrderActionMenuOpen = (event, order) => {
  event.stopPropagation();
  setOrderActionMenuAnchor(event.currentTarget);
  setOrderActionMenuRow(order);
};

const handleOrderActionMenuClose = () => {
  setOrderActionMenuAnchor(null);
};

const handleOrderQuickAction = (type) => {
  setOrderActionMenuAnchor(null);
  if (type === 'status') setOrderStatusDialogOpen(true);
};

// Define columns for Orders DataGrid (without client columns)
const orderColumns = [
  {
    field: 'OrderNumber',
    headerName: orderFieldsSchema?.OrderNumber?.label || `מספר ${orderLabel}`,
    flex: 1,
    minWidth: 120,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => (
      <Button
        variant="text"
        color="primary"
        onClick={() => navigate(`/order/${params.id}`, { state: { from: `/client/${business_id}` } })}
        sx={{ textDecoration: 'underline', cursor: 'pointer' }}
      >
        {params.value}
      </Button>
    ),
  },
  {
    field: 'OrderDate',
    headerName: orderFieldsSchema?.OrderDate?.label || `תאריך ${orderLabel}`,
    flex: 1.5,
    minWidth: 150,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => {
      if (!params.value) return '';
      const date = new Date(params.value);
      const displayDate = `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()}`;
      return (
        <Tooltip title={displayDate} componentsProps={{ tooltip: { sx: { fontSize: '1.15rem', padding: '10px 16px', backgroundColor: '#fff', color: '#333', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', border: '1px solid #e0e0e0' } } }}>
          <span style={{ whiteSpace: 'nowrap', fontSize: '0.95rem', direction: 'ltr', display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
            {displayDate}
          </span>
        </Tooltip>
      );
    },
    sortComparator: (v1, v2) => {
      const date1 = v1 ? new Date(v1).getTime() : 0;
      const date2 = v2 ? new Date(v2).getTime() : 0;
      return date1 - date2;
    }
  },
  {
    field: 'Status',
    headerName: orderFieldsSchema?.Status?.label || 'סטטוס',
    flex: 1,
    minWidth: 120,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => {
      const statusColors = getStatusColor(params.value);
      return (
        <Chip
          label={getStatusName(params.value)}
          sx={{
            backgroundColor: statusColors.bg,
            color: statusColors.color,
            borderColor: statusColors.color,
            fontWeight: 'medium',
          }}
          variant="outlined"
          size="small"
        />
      );
    },
  },
  {
    field: 'TotalAmount',
    headerName: orderFieldsSchema?.TotalAmount?.label || 'סכום כולל',
    flex: 1,
    minWidth: 130,
    type: 'number',
    align: 'center',
    headerAlign: 'center',
    valueFormatter: (value) => {
      if (value === undefined || value === null) return '';
      return new Intl.NumberFormat('he-IL', {
        style: 'currency',
        currency: 'ILS',
      }).format(value);
    },
  },
  {
    field: 'PaidAmount',
    headerName: orderFieldsSchema?.PaidAmount?.label || 'סכום ששולם',
    flex: 1,
    minWidth: 130,
    type: 'number',
    align: 'center',
    headerAlign: 'center',
    mobile: false,
    valueFormatter: (value) => {
      if (value === undefined || value === null) return '';
      return new Intl.NumberFormat('he-IL', {
        style: 'currency',
        currency: 'ILS',
      }).format(value);
    },
  },
  {
    field: 'PaymentStatus',
    headerName: 'מצב תשלום',
    flex: 1,
    minWidth: 130,
    align: 'center',
    headerAlign: 'center',
    mobile: false,
    valueGetter: (value, row) => {
      return getPaymentStatus(row.TotalAmount, row.PaidAmount);
    },
    renderCell: (params) => {
      const paymentStatus = getPaymentStatusDisplay(params.value);
      return (
        <Chip
          label={paymentStatus.label}
          sx={{
            backgroundColor: paymentStatus.bg,
            color: paymentStatus.color,
            borderColor: paymentStatus.color,
            fontWeight: 'medium',
          }}
          variant="outlined"
          size="small"
        />
      );
    },
    sortComparator: (v1, v2) => {
      // Sort by payment status: unpaid, partial_paid, fully_paid
      const order = { unpaid: 0, partial_paid: 1, fully_paid: 2, no_payment: 3 };
      return order[v1] - order[v2];
    },
  },
  {
    field: 'DueDate',
    headerName: orderFieldsSchema?.DueDate?.label || 'תאריך אספקה',
    flex: 1.5,
    minWidth: 150,
    align: 'center',
    headerAlign: 'center',
    mobile: false,
    renderCell: (params) => {
      if (!params.value) return '–';
      const date = new Date(params.value);
      const displayDate = `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()}`;
      return (
        <Tooltip title={displayDate} componentsProps={{ tooltip: { sx: { fontSize: '1.15rem', padding: '10px 16px', backgroundColor: '#fff', color: '#333', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', border: '1px solid #e0e0e0' } } }}>
          <span style={{ whiteSpace: 'nowrap', fontSize: '0.95rem' }}>
            {displayDate}
          </span>
        </Tooltip>
      );
    },
  },
];

const FinalColumns = [];

if (allowOrderManagement) {
  FinalColumns.push({
    field: 'actions',
    headerName: '',
    width: 60,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => (
      <IconButton
        size="small"
        onClick={(e) => handleOrderActionMenuOpen(e, params.row)}
        aria-label="פעולות מהירות"
      >
        <MoreVertIcon />
      </IconButton>
    ),
  });
}

orderColumns.forEach(col => {
  if (orderFields.includes(col.field)) {
    FinalColumns.push(col);
  }
  if (col.field == "ClientName" || col.field == "ClientBusinessID") {
    FinalColumns.push(col);
  }
});

  // Mobile card renderer for orders
  const orderCardRenderer = (row) => {
    const total = new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(row.TotalAmount || 0);
    const dateStr = row.OrderDate ? new Date(row.OrderDate).toLocaleDateString('he-IL') : '';
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            #{row.OrderNumber}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              {total}
            </Typography>
            {allowOrderManagement && (
              <IconButton
                size="small"
                onClick={(e) => handleOrderActionMenuOpen(e, row)}
                aria-label="פעולות מהירות"
                sx={{ flexShrink: 0 }}
              >
                <MoreVertIcon />
              </IconButton>
            )}
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {dateStr}{row.Status ? ` · ${getStatusName(row.Status)}` : ''}
        </Typography>
      </Box>
    );
  };

  // Mobile card renderer for transactions
  const transactionCardRenderer = (row) => {
    const amount = new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(row.Amount || 0);
    const dateStr = row.ActualDate ? new Date(row.ActualDate).toLocaleDateString('he-IL') : '';
    const typeMap = { Payment: 'תשלום', Charge: 'חיוב', Credit: 'זיכוי' };
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            {typeMap[row.Type] || row.Type}
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: row.Type === 'Payment' ? 'success.main' : 'error.main' }}>
            {amount}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {dateStr}{row.Description ? ` · ${row.Description}` : ''}
        </Typography>
      </Box>
    );
  };

  // 4. Proper cache invalidation function
  const invalidateClientListCache = async () => {
    await queryClient.invalidateQueries({
      queryKey: ['clients'],
      refetchType: 'active', // Ensures active queries refetch immediately
    });
  };

  // Custom toolbar component for Orders grid
  function OrdersToolbar({ onRefresh }) {
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
        <Tooltip title="רענן">
          <IconButton onClick={onRefresh}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </GridToolbarContainer>
    );
  }

  // Helper function to convert grid sortModel to API ordering parameter (for orders)
  const getOrderingFromSortModel = (sortModel) => {
    if (!sortModel || sortModel.length === 0) {
      return '-OrderDate';  // Default: newest first
    }
    const { field, sort } = sortModel[0];
    return sort === 'desc' ? `-${field}` : field;
  };

  // Handle order sort model changes
  const handleOrderSortModelChange = useCallback((newSortModel) => {
    const newOrdering = getOrderingFromSortModel(newSortModel);
    setOrderOrdering(newOrdering);
    setOrderPaginationModel(prev => ({ ...prev, page: 0 }));  // Reset to page 0 on sort change
  }, []);

  // Handle transaction sort model changes
  const handleTransactionSortModelChange = useCallback((newSortModel) => {
    setTransactionSortModel(newSortModel);
    // Reset to page 0 when sort changes
    setTransactionPageNumber(0);
  }, []);

  // Handle transaction deletion
  const handleDeleteTransaction = async () => {
    if (!transactionDeletePassword || !deletingTransaction) {
      setTransactionDeleteError('נדרשת סיסמה למחיקה');
      return;
    }

    setIsDeletingTransaction(true);
    setTransactionDeleteError('');

    try {
      await API.delete('/transaction/delete/', {
        data: {
          transaction_id: deletingTransaction.TrID,
          password: transactionDeletePassword
        }
      });

      // Refresh transactions and client data (debt)
      refreshTransactions();
      setRefreshKey(prev => prev + 1);

      // Close dialog and reset state
      setDeleteTransactionDialogOpen(false);
      setDeletingTransaction(null);
      setTransactionDeletePassword('');
    } catch (err) {
      console.error('שגיאה במחיקת התנועה:', err);
      setTransactionDeleteError(
        err?.response?.data?.error ||
        err.message ||
        'שגיאה בעת מחיקת התנועה'
      );
    } finally {
      setIsDeletingTransaction(false);
    }
  };

  // Handle order pagination model changes (page or pageSize)
  const handleOrderPaginationModelChange = useCallback((newModel) => {
    // Scroll to table top INSTANTLY before pagination changes to prevent layout shift
    if (ordersTableRef.current) {
      const element = ordersTableRef.current;
      const y = element.getBoundingClientRect().top + window.pageYOffset - 20;
      window.scrollTo({ top: Math.max(0, y), behavior: 'auto' }); // 'auto' = instant
    }

    setOrderPaginationModel(prevModel => {
      // If pageSize changed, reset to page 0
      if (newModel.pageSize !== prevModel.pageSize) {
        return { page: 0, pageSize: newModel.pageSize };
      }
      // Otherwise, just update to the new model
      return newModel;
    });
  }, []);

  // Handle transaction pagination model changes (page or pageSize)
  const handleTransactionPaginationModelChange = useCallback((newModel) => {
    // Scroll to table top INSTANTLY before pagination changes to prevent layout shift
    if (transactionsTableRef.current) {
      const element = transactionsTableRef.current;
      const y = element.getBoundingClientRect().top + window.pageYOffset - 20;
      window.scrollTo({ top: Math.max(0, y), behavior: 'auto' }); // 'auto' = instant
    }

    setTransactionPageNumber(newModel.page);
    setTransactionPageSize(newModel.pageSize);
  }, []);

  // Debounced callback for order autocomplete search
  const debouncedSetOrderSearch = useCallback((value) => {
    const timer = setTimeout(() => {
      setDebouncedOrderSearch(value);
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  // React Query for order autocomplete (server-side search)
  const {
    data: ordersAutocompleteData = [],
    isLoading: isLoadingOrderAutocomplete,
  } = useQuery({
    queryKey: ['orders', 'autocomplete', business_id, debouncedOrderSearch],
    queryFn: () => fetchOrdersForAutocompleteAPI(business_id, debouncedOrderSearch),
    enabled: !!business_id,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Create availableOrders from query data with "no results" fallback
  const availableOrders = useMemo(() => {
    if (ordersAutocompleteData.length === 0 && debouncedOrderSearch) {
      return [{ id: -1, number: 'אין תוצאות', isNoResults: true }];
    }
    return ordersAutocompleteData.map(order => ({
      id: order.OrderID,
      number: order.OrderNumber,
    }));
  }, [ordersAutocompleteData, debouncedOrderSearch]);

  // Fetch orders for this client (paginated)
  const fetchClientOrders = useCallback(async (overrideFilters = null) => {
    if (!business_id) return;

    setLoadingOrders(true);
    try {
      const params = new URLSearchParams({
        page: orderPaginationModel.page + 1,  // Convert 0-based to 1-based
        page_size: orderPaginationModel.pageSize,
        ordering: orderOrdering,
      });

      // Always set the client business ID filter
      params.append('ClientBusinessID', business_id);

      // Use override filters if provided, otherwise use current state
      const filters = overrideFilters !== null ? overrideFilters : {
        Status: orderFilters.Status,
        startDate: orderFilters.startDate,
        endDate: orderFilters.endDate,
        orderIdForFiltering: orderIdForFiltering,
        orderNumberInput: orderNumberInputRef.current,
      };

      // Add other filters
      if (filters.Status) params.append('Status', filters.Status);

      // Always include date filters, even on initial load
      if (filters.startDate instanceof Date && !isNaN(filters.startDate)) {
        params.append('start_date', format(filters.startDate, 'yyyy-MM-dd'));
      }
      if (filters.endDate instanceof Date && !isNaN(filters.endDate)) {
        params.append('end_date', format(filters.endDate, 'yyyy-MM-dd'));
      }

      // Add OrderID parameter if present
      if (filters.orderIdForFiltering) {
        params.append('OrderID', filters.orderIdForFiltering);
      }

      // Add order_number parameter if searching by order number (not ID)
      if (filters.orderNumberInput && !filters.orderIdForFiltering) {
        params.append('order_number', filters.orderNumberInput.trim());
      }

      const queryString = params.toString();
      const url = `/orders/list/?${queryString}`;

      const response = await API.get(url);

      // Extract paginated data
      setClientOrders(response.data.results || []);
      setOrderTotalRows(response.data.count || 0);
    } catch (error) {
      console.error('Error fetching client orders:', error);
      setClientOrders([]);
      setOrderTotalRows(0);
    } finally {
      setLoadingOrders(false);
    }
  }, [business_id, orderFilters, orderIdForFiltering, orderPaginationModel, orderOrdering]);

  // Fetch order summary (for managers)
  const fetchOrderSummary = useCallback(async (overrideFilters = null) => {
    if (!business_id || role !== 'manager') return;

    try {
      const params = new URLSearchParams();

      // Always set the client business ID filter
      params.append('ClientBusinessID', business_id);

      // Use override filters if provided, otherwise use current state
      const filters = overrideFilters !== null ? overrideFilters : {
        Status: orderFilters.Status,
        startDate: orderFilters.startDate,
        endDate: orderFilters.endDate,
        orderIdForFiltering: orderIdForFiltering,
        orderNumberInput: orderNumberInputRef.current,
      };

      // Add other filters
      if (filters.Status) params.append('Status', filters.Status);

      // Always include date filters
      if (filters.startDate instanceof Date && !isNaN(filters.startDate)) {
        params.append('start_date', format(filters.startDate, 'yyyy-MM-dd'));
      }
      if (filters.endDate instanceof Date && !isNaN(filters.endDate)) {
        params.append('end_date', format(filters.endDate, 'yyyy-MM-dd'));
      }

      // Add OrderID parameter if present
      if (filters.orderIdForFiltering) {
        params.append('OrderID', filters.orderIdForFiltering);
      }

      // Add order_number parameter if searching by order number (not ID)
      if (filters.orderNumberInput && !filters.orderIdForFiltering) {
        params.append('order_number', filters.orderNumberInput.trim());
      }

      const queryString = params.toString();
      const url = `/orders/summary/?${queryString}`;

      const response = await API.get(url);
      // Transform API response (snake_case) to UI format (camelCase)
      const data = response.data || {};
      setOrderSummary({
        totalOrders: data.total_orders || 0,
        totalAmount: data.total_amount || 0,
      });
    } catch (error) {
      console.error('Error fetching order summary:', error);
      setOrderSummary({ totalOrders: 0, totalAmount: 0 });
    }
  }, [business_id, orderFilters, orderIdForFiltering, role]);

  // Handle filter changes
  const handleOrderFilterChange = (field, value) => {
    setOrderFilters(prev => {
      const newFilters = {
        ...prev,
        [field]: value
      };
      // Persist to sessionStorage
      try {
        sessionStorage.setItem(ORDER_FILTERS_KEY, JSON.stringify(newFilters));
      } catch (e) {
        console.error('Failed to save order filters to sessionStorage', e);
      }
      return newFilters;
    });
  };

  // Apply filters
  const handleApplyOrderFilters = () => {
    // Reset page to 0 when filters change
    setOrderPaginationModel(prev => ({ ...prev, page: 0 }));
    const currentOrderInput = orderNumberInputRef.current;

    // If orderNumberInput exists but no orderIdForFiltering,
    // try to find matching orderId from availableOrders
    if (currentOrderInput && !orderIdForFiltering) {
      // Trim whitespace for exact matching
      const trimmedInput = currentOrderInput.trim();

      const matchingOrder = availableOrders.find(
        order => order.number === trimmedInput
      );
      if (matchingOrder) {
        // Found exact match in availableOrders - use its OrderID for precise matching
        setOrderIdForFiltering(matchingOrder.id.toString());
      }
      // If not found in availableOrders, we'll search by order_number parameter
      // which will be added in fetchClientOrders via the orderNumberInput
      // Backend will filter by user's group automatically
    }

    // If order number is being used, clear other filters
    if (currentOrderInput) {
      setOrderFilters(prev => ({
        ...prev,
        Status: '',
        startDate: null,
        endDate: null,
      }));

      // Fetch with order number only (cleared date/status filters)
      const filterOverride = {
        Status: '',
        startDate: null,
        endDate: null,
        orderIdForFiltering: orderIdForFiltering,
        orderNumberInput: orderNumberInputRef.current,
      };

      fetchClientOrders(filterOverride);
      fetchOrderSummary(filterOverride);
    } else {
      // Fetch with current filters - no override needed
      fetchClientOrders();
      fetchOrderSummary();
    }
  };


  // Reset filters
  const handleResetOrderFilters = () => {
    // Clear order number & ID
    setOrderNumberInput('');
    orderNumberInputRef.current = '';
    setOrderIdForFiltering('');
    setDebouncedOrderSearch('');
    setOrderAutocompleteKey(prev => prev + 1);  // Force Autocomplete to reset

    // Clear other filters - dates default to null
    setOrderFilters({
      Status: '',
      startDate: null,
      endDate: null,
    });

    // Reset pagination
    setOrderPaginationModel({ page: 0, pageSize: 25 });

    // Clear the cached filters from sessionStorage
    try {
      sessionStorage.removeItem(ORDER_FILTERS_KEY);
      sessionStorage.removeItem(ORDER_DATE_CACHE_KEY);
    } catch (e) {
      console.error('Failed to clear order filters from sessionStorage', e);
    }

    // Note: fetchClientOrders will be called automatically via reactive useEffect
  };

  const hasAppliedFilters = () => {
    return orderFilters.Status ||
           orderFilters.startDate ||
           orderFilters.endDate ||
           orderIdForFiltering;
  };

  const resetAndFetchOrders = () => {
    // 1. Clear all input state variables first
    setOrderNumberInput('');
    orderNumberInputRef.current = '';
    setOrderIdForFiltering('');
    setDebouncedOrderSearch('');
    setOrderAutocompleteKey(prev => prev + 1);  // Force Autocomplete to reset

    // 2. Reset orderFilters - important for the UI inputs
    const defaultFilters = {
      Status: '',
      startDate: null,
      endDate: null,
    };
    setOrderFilters(defaultFilters);

    // Clear the cached filters and date cache from sessionStorage
    try {
      sessionStorage.removeItem(ORDER_FILTERS_KEY);
      sessionStorage.removeItem(ORDER_DATE_CACHE_KEY);
    } catch (e) {
      console.error('Failed to clear order filters from sessionStorage', e);
    }

    // 3. Force reset Autocomplete value to null
    // This is needed because Autocomplete maintains its own internal state
    const autocompleteElement = document.querySelector('[role="combobox"]');
    if (autocompleteElement) {
      autocompleteElement.value = '';
    }

    // 4. Reset pagination and sorting
    // The reactive useEffects will automatically fetch orders when these state changes
    setOrderPaginationModel({ page: 0, pageSize: 25 });
    setOrderOrdering('-OrderDate');

    // Note: No direct API call needed - the reactive useEffect at line ~1298
    // will trigger fetchClientOrders() when orderFilters changes
    // and clear the summary when no date range is set
  };

  // Get status chip color - required for status column
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return { bg: '#fff3e0', color: '#ff9800' };
      case 'processing':
        return { bg: '#e1f5fe', color: '#0288d1' };
      case 'completed':
        return { bg: '#e8f5e9', color: '#4caf50' };
      case 'cancelled':
        return { bg: '#ffebee', color: '#f44336' };
      default:
        return { bg: '#f5f5f5', color: '#757575' };
    }
  };

  // Get status display name - required for status column
  const getStatusName = (status) => {
    const statusOptions = orderFieldsSchema?.Status?.options || [
      { value: 'pending', label: 'בתהליך' },
      { value: 'processing', label: 'בטיפול' },
      { value: 'completed', label: 'הושלם' },
      { value: 'cancelled', label: 'בוטל' },
    ];
    const option = statusOptions.find(opt => opt.value === status);
    return option?.label || status;
  };

  // Get payment status - required for payment status column
  const getPaymentStatus = (totalAmount, paidAmount) => {
    const total = parseFloat(totalAmount) || 0;
    const paid = parseFloat(paidAmount) || 0;
    
    if (total === 0) return 'no_payment';
    if (paid >= total) return 'fully_paid';
    if (paid > 0) return 'partial_paid';
    return 'unpaid';
  };

  // Get payment status display - required for payment status column
  const getPaymentStatusDisplay = (status) => {
    switch (status) {
      case 'fully_paid':
        return { label: 'שולם במלואו', bg: '#e8f5e9', color: '#4caf50' };
      case 'partial_paid':
        return { label: 'שולם חלקית', bg: '#e1f5fe', color: '#0288d1' };
      case 'unpaid':
        return { label: 'לא שולם', bg: '#ffebee', color: '#f44336' };
      case 'no_payment':
        return { label: 'ללא תשלום', bg: '#f5f5f5', color: '#757575' };
      default:
        return { label: 'לא ידוע', bg: '#f5f5f5', color: '#757575' };
    }
  };
  // =============== NEW: Restore Client Function ===============
  const handleOpenRestoreDialog = () => {
    setRestoreDialogOpen(true);
    setRestoreError('');
    setRestoreSuccess('');
  };

  const handleCancelRestore = () => {
    setRestoreDialogOpen(false);
  };

  const handleConfirmRestore = async () => {
    setRestoreLoading(true);
    setRestoreError('');
    setRestoreSuccess('');
    
    try {
      await API.post('/client/restore/', { business_id });
      setRestoreSuccess('הלקוח שוחזר בהצלחה!');
      
      // Invalidate cache
      await invalidateClientListCache();
      
      // Close the dialog
      setTimeout(() => {
        setRestoreDialogOpen(false);
        
        // Force a complete reload of the page to ensure fresh data
        window.location.href = `/client/${business_id}`;
      }, 1000);
    } catch (error) {
      console.error('Error restoring client:', error);
      setRestoreError(error?.response?.data?.error || 'שגיאה בשחזור הלקוח');
      setRestoreLoading(false);
    }
  };

  // If client not found, display message and then redirect
  if (clientNotFound) {
    return (
      <Container sx={{ mt: 4 }}>
        <Typography variant="h6">
          לקוח לא נמצא או מתעדכן, מעביר לעמוד לקוחות...
        </Typography>
      </Container>
    );
  }

  if (!client) {
    return (
      <Container sx={{ mt: 4 }}>
        <Typography variant="h6">טוען...</Typography>
      </Container>
    );
  }

  // Helper
  const renderGroupChip = (group) => {
    if (!group) return null;
    if ( groupAccessRoles.includes(role)  && !isClientDeleted) {
      return (
        <Chip
          key={group.GroupID}
          component={Link}
          to={`/group/${group.GroupID}`}
          clickable
          label={group.GroupName}
          variant="outlined"
          sx={{
            borderRadius: '9999px',
            mr: 1,
            mb: 1,
            fontSize: '0.9rem',
            fontWeight: 'bold',
            color: '#1976d2',
            borderColor: 'currentColor',
            backgroundColor: '#f8f9fa',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: '#e9ecef',
              color: '#1565c0',
              '& .MuiChip-label': {
                textDecoration: 'underline',
              },
            },
            '&.Mui-visited': {
              color: '#6a1b9a',
            },
          }}
        />
      );
    } else {
      return (
        <Chip
          key={group.GroupID}
          label={group.GroupName}
          variant="outlined"
          sx={{
            borderRadius: '9999px',
            mr: 1,
            mb: 1,
            fontSize: '0.9rem',
            fontWeight: 'bold',
            color: '#1976d2',
            borderColor: 'currentColor',
            backgroundColor: '#f8f9fa',
            cursor: isClientDeleted ? 'default' : 'pointer',
            '&:hover': !isClientDeleted ? {
              backgroundColor: '#e9ecef',
              color: '#1565c0',
              '& .MuiChip-label': {
                textDecoration: 'underline',
              },
            } : {},
            '&.Mui-visited': {
              color: '#6a1b9a',
            },
          }}
        />
      );
    }
  };

  // ==============================================
  // Debt Editing - Refactored to use UI config
  // ==============================================
  const handleOpenDebtEdit = () => {
    if (!client.debt || !client.debt.DebtFields) return;
    
    // Initialize debt field values from client data
    const initialDebtValues = {};
    debtFields.forEach(field => {
      if (field !== 'TotalDebt') { // TotalDebt is calculated
        initialDebtValues[field] = client.debt.DebtFields[field] || 0;
      }
    });
    setDebtFieldValues(initialDebtValues);

    setUserNote('');
    setErrorMsg('');
    setSuccessMsg('');
    setConfirmStep(false);
    setEditDialogOpen(true);
  };

  const handleDebtFieldChange = (field, value) => {
    setDebtFieldValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveUpdates = () => {
    setConfirmStep(true);
    setErrorMsg('');
    setSuccessMsg('');
  };

  const handleGoBack = () => {
    setConfirmStep(false);
    setErrorMsg('');
    setSuccessMsg('');
  };

  const handleConfirmUpdates = async () => {
    setIsUpdatingDebt(true);
    if (confirmDebtButtonRef.current) {
      confirmDebtButtonRef.current.disabled = true;
    }
    setErrorMsg('');
    setSuccessMsg('');
    try {
      // Create updates array from debt field values
      const updates = Object.entries(debtFieldValues).map(([field, value]) => ({
        field,
        value: parseFloat(value) || 0
      }));

      const payload = {
        business_id: client.ClientBusinessID,
        updates,
        note: userNote,
      };

      await API.put('/client/update-debt/', payload);

      await invalidateClientListCache();

      setSuccessMsg('החוב עודכן בהצלחה!');

      const updatedClient = await API.get(`/client/?business_id=${client.ClientBusinessID}`);
      setClient(updatedClient.data);

      // A short delay so the user sees success before dialog closes
      setTimeout(() => {
        setEditDialogOpen(false);
        window.location.reload();
      }, 1500);
    } catch (err) {
      console.error('Error updating debt:', err);
      setErrorMsg(err?.response?.data?.error || 'שגיאה לא ידועה');
    } finally {
      setIsUpdatingDebt(false);
      if (confirmDebtButtonRef.current) {
        confirmDebtButtonRef.current.disabled = false;
      }
    }
  };

  const handleCancel = () => {
    setEditDialogOpen(false);
    setConfirmStep(false);
    setErrorMsg('');
    setSuccessMsg('');
  };

  // ==============================================
  // Client Info Editing - Refactored to use UI config
  // ==============================================
  const handleOpenClientEdit = () => {
    // Initialize client field values
    const initialClientValues = {};
    const originalValues = {};
    
    clientFields.forEach(field => {
      // Only include editable fields
      if (clientFieldsSchema[field] && clientFieldsSchema[field].readonly === false) {
        // Force ClientBusinessID as readonly when auto-generate is enabled
        if (field === 'ClientBusinessID' && autoGenerateId) return;
        initialClientValues[field] = client[field] || '';
        originalValues[field] = client[field] || '';
      }
    });
    
    setClientFieldValues(initialClientValues);
    setOriginalClientFieldValues(originalValues);

    setClientErrorMsg('');
    setClientSuccessMsg('');
    setClientConfirmStep(false);
    setClientEditDialogOpen(true);
  };

  const handleClientFieldChange = (field, value) => {
    setClientFieldValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveClientUpdates = () => {
    // Validation logic
    let hasError = false;
    
    // Check if any required fields are empty
    clientFields.forEach(field => {
      const schema = clientFieldsSchema[field];
      if (schema && schema.readonly === false && schema.required === true) {
        if (!clientFieldValues[field]) {
          setClientErrorMsg('אחד או יותר מן השדות הנדרשים ריקים, אנא מלא/י את כל השדות הנדרשים.');
          hasError = true;
        }
      }
    });
    
    // Special validation for ClientBusinessID
    if (clientFieldValues.ClientBusinessID && 
      clientFieldValues.ClientBusinessID !== originalClientFieldValues.ClientBusinessID) {
    
    // Always check that the value contains only digits
    if (!/^\d*$/.test(clientFieldValues.ClientBusinessID.trim())) {
      setClientErrorMsg('מזהה חייב להכיל ספרות בלבד.');
      hasError = true;
    }
    // Only validate ID length if enforce_id is true
    else if (enforceIdValidation && 
             clientFieldValues.ClientBusinessID.trim().length !== 9) {
      setClientErrorMsg('חפ/תז חייב להיות 9 ספרות.');
      hasError = true;
    }
  }
    
    if (!hasError) {
      setClientConfirmStep(true);
      setClientErrorMsg('');
      setClientSuccessMsg('');
    }
  };

  const handleGoBackClient = () => {
    setClientConfirmStep(false);
    setClientErrorMsg('');
    setClientSuccessMsg('');
  };

  const handleConfirmClientUpdates = async () => {
    setIsUpdatingClient(true);
    if (confirmClientButtonRef.current) {
      confirmClientButtonRef.current.disabled = true;
    }
    setClientErrorMsg('');
    setClientSuccessMsg('');
    
    try {
      const fieldsToUpdate = [];

      // Check which fields have changed
      Object.keys(clientFieldValues).forEach(field => {
        if (clientFieldValues[field] !== originalClientFieldValues[field]) {
          fieldsToUpdate.push({
            field,
            value: clientFieldValues[field]
          });
        }
      });

      // If user didn't actually change anything, we handle it as a success
      if (fieldsToUpdate.length === 0) {
        setClientSuccessMsg('לא בוצעו שינויים.');
        // Hide the dialog soon after
        setTimeout(() => {
          setClientEditDialogOpen(false);
        }, 1200);
        return;
      }

      // Sort fields to ensure ClientBusinessID is always updated LAST
      fieldsToUpdate.sort((a, b) => {
        if (a.field === 'ClientBusinessID') return 1;  // Push to end
        if (b.field === 'ClientBusinessID') return -1; // Keep others before
        return 0;
      });

      // Update each changed field
      for (const item of fieldsToUpdate) {
        await API.put('/client/update/', {
          business_id: client.ClientBusinessID, // Current ID used to locate the client record
          field: item.field,
          value: item.value,
        });
      }

      // Rest of your existing code...
      await invalidateClientListCache();
      setClientSuccessMsg('פרטי הלקוח עודכנו בהצלחה!');

      // If business ID was updated, refresh page with new ID
      if (clientFieldValues.ClientBusinessID !== originalClientFieldValues.ClientBusinessID) {
        setTimeout(() => {
          setClientEditDialogOpen(false);
          navigate(`/client/${clientFieldValues.ClientBusinessID}`);
        }, 1000);
      } else {
        const updatedClient = await API.get(`/client/?business_id=${client.ClientBusinessID}`);
        setClient(updatedClient.data);
        setTimeout(() => {
          setClientEditDialogOpen(false);
          window.location.reload();
        }, 1000);
      }
    } catch (err) {
      console.error('Error updating client details:', err);
      setClientErrorMsg(err?.response?.data?.error || 'שגיאה לא ידועה');
    } finally {
      setIsUpdatingClient(false);
      if (confirmClientButtonRef.current) {
        confirmClientButtonRef.current.disabled = false;
      }
    }
  };

  const handleCancelClient = () => {
    setClientEditDialogOpen(false);
    setClientConfirmStep(false);
    setClientErrorMsg('');
    setClientSuccessMsg('');
  };

  // ==============================================
  // NEW: Two-step Client Delete Functions
  // ==============================================
  const handleOpenDeleteDialog = () => {
    setDeleteDialogOpen(true);
    setDeleteStep(1);
    setDeletePassword('');
    setDeleteErrorMsg('');
    setDeleteSuccessMsg('');
  };

  const handleCancelDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeleteStep(1);
    setDeletePassword('');
    setDeleteErrorMsg('');
    setDeleteSuccessMsg('');
    setDeleteLoading(false);
    setPasswordVisible(false);
  };

  // Step 1: "Are you sure?"
  const handleConfirmDeleteStep1 = () => {
    setDeleteErrorMsg('');
    setDeleteSuccessMsg('');
    setDeleteStep(2);
  };

  // Step 2: user enters password, final delete
  const handleConfirmDeleteStep2 = async () => {
    setDeleteLoading(true);
    setDeleteErrorMsg('');
    setDeleteSuccessMsg('');

    try {
      // We now send `password` to the server
      await API.delete('/client/delete/', {
        data: {
          business_id: client.ClientBusinessID,
          password: deletePassword,
        },
      });
      setDeleteSuccessMsg('הלקוח נמחק בהצלחה!');
      await invalidateClientListCache();

      setTimeout(() => {
        setDeleteDialogOpen(false);
        navigate('/clients');
      }, 1000);
    } catch (error) {
      console.error('Error deleting client:', error);
      // If the server returns 401 for an invalid password, show Hebrew msg:
      if (error.response && error.response.status === 401) {
        setDeleteErrorMsg('סיסמה שגויה');
      } else {
        setDeleteErrorMsg(
          error?.response?.data?.error || 'שגיאה במחיקת הלקוח'
        );
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  // ==============================================
  // NEW: Client Comments Functions
  // ==============================================
  const handleSaveComments = async () => {
    setCommentsError('');
    setCommentsSuccess('');
    setCommentsLoading(true);
    try {
      await API.put('/client/update/', {
        business_id: client.ClientBusinessID,
        field: 'ClientComments',
        value: comments,
      });
      await invalidateClientListCache();

      setCommentsSuccess('הערות עודכנו בהצלחה!');
      const updatedClient = await API.get(`/client/?business_id=${client.ClientBusinessID}`);
      setClient(updatedClient.data);
      setComments(updatedClient.data.ClientComments || '');
      setEditingComments(false);
    } catch (error) {
      setCommentsError(error?.response?.data?.error || 'שגיאה בעדכון הערות');
    } finally {
      setCommentsLoading(false);
    }
  };

  // ==============================================
  // NEW: Text formatting function for comment editing
  // (currently unused but left as an example)
  // ==============================================
  const applyFormat = (formatType) => {
    if (!textFieldRef.current) return;
    const textarea = textFieldRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const before = comments.substring(0, start);
    const selected = comments.substring(start, end);
    const after = comments.substring(end);
    let formatted = '';
    if (formatType === 'bold') {
      formatted = `**${selected}**`;
    } else if (formatType === 'italic') {
      formatted = `_${selected}_`;
    } else if (formatType === 'underline') {
      formatted = `<u>${selected}</u>`;
    } else if (formatType === 'ordered') {
      const lines = selected.split('\n');
      formatted = lines.map((line, index) => `${index + 1}. ${line}`).join('\n');
    }
    const newText = before + formatted + after;
    setComments(newText);
  };

  // NEW: Clear order number filter
  const handleClearOrderFilter = () => {
    setOrderNumberFilter(null);
  };

  // Check if any transaction filters are applied
  const hasAppliedTransactionFilters = () => {
    return (startDate instanceof Date && !isNaN(startDate)) ||
           (endDate instanceof Date && !isNaN(endDate)) ||
           transactionType !== 'All' ||
           debtTypeFilter !== 'All' ||
           orderNumberFilter;
  };

  // Reset all transaction filters
  const handleResetTransactionFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setTransactionType('All');
    setDebtTypeFilter('All');
    setOrderNumberFilter(null);
    setTransactionPageNumber(0);

    // Clear the cached filters from sessionStorage
    try {
      sessionStorage.removeItem(TRANSACTION_FILTERS_KEY);
      sessionStorage.removeItem(TRANSACTION_DATE_CACHE_KEY);
    } catch (e) {
      console.error('Failed to clear transaction filters from sessionStorage', e);
    }
  };

  // =============== NEW: PRINT FUNCTION ===============
  // Fetches ALL filtered transactions (not just current page) and prints them
  const handlePrintPage = async () => {
    // Determine sort order:
    // 1. First check UI config for transaction_print_sort_order
    // 2. If not configured, use current DataGrid sort model
    // 3. Default to 'desc' (newest first)
    let sortOrder = 'desc'; // default

    const configSortOrder = uiConfig?.pages_configs?.ClientDetailPage?.transaction_print_sort_order;
    if (configSortOrder === 'asc' || configSortOrder === 'desc') {
      // UI config takes priority
      sortOrder = configSortOrder;
    } else if (transactionSortModel && transactionSortModel.length > 0) {
      // Fall back to current DataGrid sort model
      const sortConfig = transactionSortModel[0];
      if (sortConfig.field === 'ActualDate') {
        sortOrder = sortConfig.sort;
      }
    }

    // Fetch ALL transactions matching current filters using dedicated print endpoint
    setPrintLoading(true);
    try {
      const params = {
        business_id: business_id,
      };

      if (transactionType !== 'All') {
        params.transaction_type = transactionType;
      }

      if (startDate instanceof Date && !isNaN(startDate)) {
        params.start_date = format(startDate, 'yyyy-MM-dd');
      }

      if (endDate instanceof Date && !isNaN(endDate)) {
        params.end_date = format(endDate, 'yyyy-MM-dd');
      }

      if (orderNumberFilter) {
        params.order_number = orderNumberFilter;
      }

      // Debt type filter
      if (debtTypeFilter !== 'All') {
        params.subtype = debtTypeFilter;
      }

      // Use sort order for API call
      params.ordering = sortOrder === 'asc' ? 'ActualDate' : '-ActualDate';

      // Use dedicated print endpoint - returns all transactions without pagination
      const response = await API.get('/transactions/print/', { params });
      const allTransactions = response.data || [];

      // Generate the HTML and then open a new window to print
      const htmlContent = generatePrintHTML(
        client,
        debtFields,
        debtFieldsSchema,
        allTransactions,
        uiConfig,
        orderNumberFilter
      );

      openPrintWindow(htmlContent);
    } catch (error) {
      console.error('Error fetching transactions for print:', error);
    } finally {
      setPrintLoading(false);
    }
  };

  // Print only the current balance + client details (no transactions).
  // Renders only fields configured in ui_config (client_fields / debt_fields).
  const handlePrintBalance = () => {
    if (!client) return;
    const html = generateBalancePrintHTML({
      client,
      clientFields,
      clientFieldsSchema,
      debtFields,
      debtFieldsSchema,
      useTotalDebt,
      businessProfile,
    });
    openPrintWindow(html);
  };

  // Helper function to build the raw HTML for printing.
  function generatePrintHTML(clientData, allDebtFields, debtSchema, txs, config, orderNumber) {
    const businessIDLabel = config.client_fields_schema?.ClientBusinessID?.label;
    const transactionFieldsSchema = config.transaction_fields_schema || {};

    // NEW: Check for custom print order in ClientDetailPage config, fallback to regular transaction_fields
    const printOrderFields = config.pages_configs?.ClientDetailPage?.transaction_print_order || config.transaction_fields || [];

    const style = `
      <style>
        @media print {
          @page {
            margin: 10mm;
          }
          thead {
            display: table-header-group;
          }
          tr {
            page-break-inside: avoid;
          }
          .transaction-row {
            page-break-inside: avoid;
          }
        }
        body {
          direction: rtl;
          font-family: Arial, sans-serif;
          font-size: 16px;
        }
        table {
          border-collapse: collapse;
          width: 100%;
        }
        th, td {
          border: 1px solid #ccc;
          padding: 6px;
          text-align: center;
          vertical-align: middle;
        }
        th {
          background-color: #f0f0f0;
          font-weight: bold;
        }
        h2, h3 {
          margin: 0;
          margin-bottom: 8px;
        }
        .section-header {
          margin-top: 24px;
          margin-bottom: 8px;
          font-size: 16px;
          font-weight: bold;
        }
        .client-info, .debt-summary, .transactions-table {
          margin-bottom: 24px;
        }
        .order-filter {
          margin-bottom: 12px;
          padding: 8px;
          background-color: #f8f8f8;
          border: 1px solid #ddd;
          border-radius: 4px;
        }
        
        /* Special styles for formatted notes */
        .formatted-note {
          font-family: monospace;
          white-space: pre-wrap;
          font-size: 10px;
          line-height: 1.4;
          padding: 8px;
          background-color: #f9f9f9;
          border: 1px solid #e0e0e0;
          margin-top: 4px;
          text-align: right;
          direction: rtl;
        }
        
        .note-cell {
          max-width: 400px;
          text-align: right !important;
          vertical-align: top;
        }
        
        /* Hide note column in main table if we're showing expanded notes */
        .compact-note {
          font-style: italic;
          color: #666;
        }
        
        .transaction-with-note {
          margin-bottom: 12px;
        }
      </style>
    `;

    // 1) Client Info Section:
    const clientInfoTable = `
      <div class="client-info">
        <h2>פרטי לקוח</h2>
        <table>
          <tbody>
            <tr>
              <th>שם לקוח</th>
              <td>${clientData.ClientName || ''}</td>
            </tr>
            <tr>
              <th>${businessIDLabel || 'מזהה לקוח'}</th>
              <td>${clientData.ClientBusinessID || ''}</td>
            </tr>
          </tbody>
        </table>
      </div>
    `;

    // Debt Summary Section
    let totalCalculated = 0;
    if (clientData.debt && clientData.debt.DebtFields) {
      totalCalculated = allDebtFields
        .filter(f => f !== 'TotalDebt')
        .reduce((sum, f) => sum + (parseFloat(clientData.debt.DebtFields[f] || 0)), 0);
    }

    const debtSummaryRows = clientData.debt && clientData.debt.DebtFields
      ? allDebtFields
          .filter(f => f !== 'TotalDebt')
          .map(fieldKey => `
            <tr>
              <td>${debtSchema[fieldKey]?.label || fieldKey}</td>
              <td>${formatCurrency(clientData.debt.DebtFields[fieldKey] || 0)}</td>
            </tr>
          `)
          .join('')
      : '';

    const debtSummaryTable = `
      <div class="debt-summary">
        <h2>סיכום חוב</h2>
        <table>
          <thead>
            <tr>
              <th>קטגוריה</th>
              <th>סכום</th>
            </tr>
          </thead>
          <tbody>
            ${debtSummaryRows || ''}
            <tr>
              <th>סה"כ</th>
              <th>${formatCurrency(totalCalculated)}</th>
            </tr>
          </tbody>
        </table>
      </div>
    `;

    // Order filter section
    let orderFilterHTML = '';
    if (orderNumber) {
      const orderNumberLabel = config.transaction_fields_schema?.OrderNumber?.label || 'מספר הזמנה';
      
      // Calculate order summary totals
      let totalAmount = 0;
      let totalCount = 0;
      
      txs.forEach(tx => {
        totalCount++;
        if (tx.Amount) {
          const amount = parseFloat(tx.Amount) || 0;
          if (tx.Type === 'Payment' || tx.Type === 'Credit') {
            totalAmount -= amount;
          } else if (tx.Type === 'Charge') {
            totalAmount += amount;
          }
        }
      });
      
      orderFilterHTML = `
        <div class="order-filter">
          <h3>${orderNumberLabel}: ${orderNumber}</h3>
          <p>מספר תנועות: ${txs.length}</p>
          <p>יתרה: ${formatCurrency(totalAmount)}</p>
        </div>
      `;
    }

    // Updated Transactions Table with print order
    // Ensure ActualDate is used instead of Timestamp for printing
    let transactionFields = [...printOrderFields];
    if (!transactionFields.includes('ActualDate')) {
      const timestampIndex = transactionFields.indexOf('Timestamp');
      if (timestampIndex !== -1) {
        transactionFields.splice(timestampIndex, 0, 'ActualDate');
      } else {
        transactionFields.push('ActualDate');
      }
    }
    // Remove Timestamp from print fields - use ActualDate instead
    transactionFields = transactionFields.filter(f => f !== 'Timestamp');

    // Check if Note field is included
    const hasNoteField = transactionFields.includes('Note');
    
    // Build table header (excluding Note if it has formatted content)
    const getFieldLabel = (fn) => {
      if (fn === 'ActualDate') return 'תאריך בפועל';
      return transactionFieldsSchema[fn]?.label || fn;
    };
    const txTableHeader = transactionFields
      .filter(fn => !['TrID','ClientName','ClientBusinessID'].includes(fn))
      .map(fn => `<th>${getFieldLabel(fn)}</th>`)
      .join('');

    // Process transactions with special handling for formatted notes
    const processedTransactions = txs.map((tx, index) => {
      const hasFormattedNote = tx.Note && tx.Note.includes('================');
      
      // Build row cells
      const rowCells = transactionFields
        .filter(fn => !['TrID','ClientName','ClientBusinessID'].includes(fn))
        .map(fn => {
          let cellValue = tx[fn] ?? '';
          
          // Format different field types
          if (fn === 'Timestamp') {
            if (cellValue) {
              const d = new Date(cellValue);
              cellValue = d.toLocaleString('he-IL');
            }
          } else if (fn === 'ActualDate') {
            if (cellValue) {
              const d = new Date(cellValue);
              cellValue = d.toLocaleDateString('he-IL');
            }
          } else if (fn === 'Type') {
            if (cellValue === 'Payment') cellValue = 'תשלום';
            else if (cellValue === 'Charge') cellValue = 'חיוב';
            else if (cellValue === 'Credit') cellValue = 'זיכוי';
          } else if (fn === 'SubType') {
              // Check all three schema sources for SubType labels
              if (debtSchema[cellValue]) {
                cellValue = debtSchema[cellValue].label;
              } else if (config.client_fields_schema && config.client_fields_schema[cellValue]) {
                cellValue = config.client_fields_schema[cellValue].label;
              } else if (config.order_transaction_fields_schema && config.order_transaction_fields_schema[cellValue]) {
                cellValue = config.order_transaction_fields_schema[cellValue].label;
              }
          } else if (['Amount','TotalAfterTR'].includes(fn) || (transactionFieldsSchema[fn]?.type === 'number')) {
            const numericVal = parseFloat(cellValue) || 0;
            if (fn === 'Amount') {
              if (tx.Type === 'Payment' || tx.Type === 'Credit') {
                cellValue = `${formatCurrency(numericVal)}-`;
              } else if (tx.Type === 'Charge') {
                cellValue = `${formatCurrency(numericVal)}+`;
              } else {
                cellValue = formatCurrency(numericVal);
              }
            } else {
              cellValue = formatCurrency(numericVal);
            }
          } else if (fn === 'Note' && hasFormattedNote) {
            // For formatted notes, show a compact indicator
            cellValue = '<span class="compact-note">ראה פרטים למטה</span>';
          }

          const cellClass = fn === 'Note' ? 'note-cell' : '';
          return `<td class="${cellClass}">${cellValue}</td>`;
        })
        .join('');

      const transactionRow = `<tr>${rowCells}</tr>`;
      
      // If this transaction has a formatted note, add it as a separate section below the row
      if (hasFormattedNote) {
        return `
          <tbody class="transaction-with-note">
            ${transactionRow}
            <tr>
              <td colspan="${transactionFields.filter(fn => !['TrID','ClientName','ClientBusinessID'].includes(fn)).length}" style="padding: 0;">
                <div class="formatted-note">${tx.Note}</div>
              </td>
            </tr>
          </tbody>
        `;
      }
      
      return `<tbody>${transactionRow}</tbody>`;
    }).join('');

    const transactionsHTML = `
      <div class="transactions-table">
        <h2>תנועות</h2>
        ${orderFilterHTML}
        <table>
          <thead>
            <tr>${txTableHeader}</tr>
          </thead>
          ${processedTransactions}
        </table>
      </div>
    `;

    // Combine all sections
    return `
      <!DOCTYPE html>
      <html dir="rtl" lang="he">
      <head>
        <meta charset="UTF-8" />
        <title>${clientData.ClientName || 'לקוח'} - פרטי לקוח</title>
        ${style}
      </head>
      <body>
        ${generateBusinessHeader(businessProfile)}
        ${clientInfoTable}
        ${debtSummaryTable}
        ${transactionsHTML}
      </body>
      </html>
    `;
  }

  function formatCurrency(num) {
    const val = parseFloat(num) || 0;
    return val.toLocaleString('he-IL', { style: 'currency', currency: 'ILS' });
  }

  function generateBalancePrintHTML({
    client: clientData,
    clientFields: cFields,
    clientFieldsSchema: cSchema,
    debtFields: dFields,
    debtFieldsSchema: dSchema,
    useTotalDebt: showTotal,
    businessProfile: bp,
  }) {
    const generatedDate = new Date().toLocaleDateString('he-IL');

    // Client details rows — only configured fields, mirroring the on-page filter
    const HIDDEN_CLIENT_FIELDS = new Set(['ClientComments', 'IsDeleted', 'DeletedAt']);
    const renderClientValue = (field, schema) => {
      const raw = clientData[field];
      if (raw === null || raw === undefined || raw === '') return '';
      if (schema.type === 'date') {
        const d = new Date(raw);
        if (isNaN(d.getTime())) return '';
        return d.toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' });
      }
      if (schema.type === 'boolean') return raw ? 'כן' : 'לא';
      return String(raw);
    };
    const clientRows = cFields
      .filter(field => !HIDDEN_CLIENT_FIELDS.has(field))
      .map(field => {
        const schema = cSchema[field];
        if (!schema) return '';
        const label = schema.label || field;
        const value = renderClientValue(field, schema);
        return `<tr><td class="key-cell">${label}</td><td>${value}</td></tr>`;
      })
      .filter(Boolean)
      .join('');

    // Hero TotalDebt — only when configured
    const hasDebt = !!clientData.debt;
    const totalDebtConfigured = showTotal && hasDebt;
    const heroLabel = dSchema.TotalDebt?.label || 'סך חוב';
    const heroSection = totalDebtConfigured ? `
      <div class="hero">
        <div class="hero-box">
          <div class="hero-label">${heroLabel}</div>
          <div class="hero-amount">${formatCurrency(clientData.debt.TotalDebt)}</div>
        </div>
      </div>
    ` : '';

    // Debt breakdown rows — only configured fields, excluding TotalDebt
    const breakdownFields = hasDebt
      ? dFields.filter(f => f !== 'TotalDebt' && dSchema[f])
      : [];
    const breakdownRows = breakdownFields
      .map(field => {
        const label = dSchema[field].label || field;
        const amount = clientData.debt.DebtFields?.[field] || 0;
        return `<tr><td>${label}</td><td dir="ltr" style="text-align:center">${formatCurrency(amount)}</td></tr>`;
      })
      .join('');
    // Only show totals row when there are 2+ rendered rows (a single row would just duplicate the value)
    const breakdownTotal = breakdownFields.reduce(
      (sum, f) => sum + parseFloat(clientData.debt.DebtFields?.[f] || 0),
      0
    );
    const totalsRow = breakdownFields.length >= 2
      ? `<tr><td style="font-weight:bold">סה"כ</td><td dir="ltr" style="text-align:center;font-weight:bold">${formatCurrency(breakdownTotal)}</td></tr>`
      : '';
    const breakdownTable = breakdownFields.length > 0 ? `
      <h2>פירוט חוב</h2>
      <table>
        <thead>
          <tr><th>קטגוריה</th><th>סכום</th></tr>
        </thead>
        <tbody>
          ${breakdownRows}
          ${totalsRow}
        </tbody>
      </table>
    ` : '';

    const noDebtNotice = !hasDebt
      ? '<p style="text-align:center;color:#888;margin:24px 0">אין מידע על חוב זמין.</p>'
      : '';

    const style = `
      <style>
        @media print {
          @page { margin: 10mm; }
          thead { display: table-header-group; }
          tr { page-break-inside: avoid; }
        }
        body {
          direction: rtl;
          font-family: Arial, sans-serif;
          font-size: 16px;
          margin: 0;
          padding: 16px;
        }
        h1 { font-size: 24px; text-align: center; margin: 8px 0 16px; }
        h2 { font-size: 18px; margin: 20px 0 8px; border-bottom: 2px solid #ddd; padding-bottom: 4px; color: #444; }
        table { border-collapse: collapse; width: 100%; margin-bottom: 16px; direction: rtl; }
        th, td { border: 1px solid #ccc; padding: 8px; text-align: center; vertical-align: middle; }
        th { background-color: #f0f0f0; font-weight: bold; }
        .key-cell { background-color: #fafafa; font-weight: 600; width: 35%; text-align: right; }
        .hero { text-align: center; margin: 16px 0 24px; }
        .hero-box {
          display: inline-block;
          border: 3px solid #1a1a1a;
          border-radius: 10px;
          padding: 14px 40px;
          background: #f5f5f5;
        }
        .hero-label { font-size: 14px; color: #666; margin-bottom: 6px; }
        .hero-amount { font-size: 22px; font-weight: 600; color: #1a1a1a; direction: ltr; }
        .footer { margin-top: 24px; font-size: 12px; text-align: center; color: #666; }
      </style>
    `;

    return `<!DOCTYPE html>
<html dir="rtl" lang="he">
<head>
  <meta charset="UTF-8" />
  <title>יתרת לקוח - ${clientData.ClientName || ''}</title>
  ${style}
</head>
<body>
  ${generateBusinessHeader(bp)}
  <h1>יתרת לקוח</h1>
  ${clientRows ? `
  <h2>פרטי לקוח</h2>
  <table>
    <tbody>${clientRows}</tbody>
  </table>` : ''}
  ${heroSection}
  ${breakdownTable}
  ${noDebtNotice}
  <div class="footer">הופק בתאריך ${generatedDate}</div>
</body>
</html>`;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={heLocale}>
      <ThemeProvider theme={theme}>
        <div dir="rtl">
          <Container
            maxWidth={false}
            sx={{
              width: '100%',
              px: { xs: 1, sm: 2 },
              my: { xs: 1, sm: 4 },
              position: 'relative'
            }}
          >
            {/* ========== NEW: Deleted Client Banner ========== */}
            {isClientDeleted && (
              <Paper
                elevation={3}
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 999,
                  mb: 4,
                  p: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  backgroundColor: '#ffebee',
                  borderRadius: 2
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
                  לקוח זה נמחק בתאריך {' '}
                  {deletedAt ? new Date(deletedAt).toLocaleDateString('he-IL', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                  }) : 'לא ידוע'}
                </Typography>
                { restoreClientRoles.includes(role) && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleOpenRestoreDialog}
                    sx={{ fontWeight: 'bold' }}
                  >
                    שחזור לקוח
                  </Button>
                )}
              </Paper>
            )}

            <Typography
              variant="h4"
              sx={{
                mb: { xs: 1.5, sm: 3 },
                fontWeight: 'bold',
                fontSize: { xs: '1.75rem !important', sm: '3rem !important' },
              }}
            >
              פרטי לקוח
            </Typography>

            <div style={{ opacity: isClientDeleted ? 0.6 : 1, pointerEvents: isClientDeleted ? 'none' : 'auto' }}>
              {/* Cards Container - Set max width here */}
              <Box sx={{
                maxWidth: { xs: '100%', md: '1200px' },
                mr: 0,
                ml: { xs: 0, md: 'auto' },
                mb: { xs: 2, sm: 4 },
              }}>
                <Grid container spacing={{ xs: 1.5, sm: 3 }} alignItems="stretch">
                  {/* Left Card: Client Info - Dynamic fields based on config */}
                  <Grid item xs={12} md={6}>
                    <Card
                      sx={{
                        borderRadius: 2,
                        boxShadow: 3,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                      }}
                    >
                      <CardHeader
                        sx={{
                          backgroundColor: 'primary.main',
                          color: 'white',
                          py: { xs: 1, sm: 1.5 },
                          px: { xs: 1.5, sm: 2 },
                        }}
                        title={
                          <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: { xs: '1rem !important', sm: '1.25rem !important' } }}>
                            מידע לקוח
                          </Typography>
                        }
                      />
                      <CardContent sx={{ flexGrow: 1, px: { xs: 1.5, sm: 2 }, py: { xs: 1, sm: 2 } }}>
                        <Grid container spacing={{ xs: 1, sm: 2 }}>
                          {/* Dynamically display client fields based on config */}
                          {clientFields
                            .filter(field => 
                              field !== 'ClientComments' && 
                              field !== 'IsDeleted' && 
                              field !== 'DeletedAt')
                            .map(field => {
                              const schema = clientFieldsSchema[field];
                              if (!schema) return null;
                              
                              // Special handling for date fields
                              if (schema.type === 'date' && client[field]) {
                                const dateStr = new Date(client[field]).toLocaleDateString('he-IL', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                });
                                // Show age next to birthdate
                                const age = field === 'BirthDate' ? calculateAge(client[field]) : null;
                                return (
                                  <Grid item xs={12} key={field}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                      {schema.label || field}:
                                    </Typography>
                                    <Typography variant="body1">
                                      {dateStr}
                                      {age !== null && ` (גיל: ${age})`}
                                    </Typography>
                                  </Grid>
                                );
                              }
                              
                              return (
                                <Grid item xs={12} key={field}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                    {schema.label || field}:
                                  </Typography>
                                  <Typography variant="body1">{client[field] || ''}</Typography>
                                </Grid>
                              );
                            })
                          }
                          
                          {/* Groups section - only show if include_groups is true */}
                          {uiConfig.include_groups && (
                            <>
                              {/* Flow group - only show if Flow type is included in group_types */}
                              {uiConfig.group_types && uiConfig.group_types.includes('Flow') && (
                                <Grid item xs={12}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                    קבוצת שוטף:
                                  </Typography>
                                  {client.FlowGroup ? renderGroupChip(client.FlowGroup) : 'לא הוגדר'}
                                </Grid>
                              )}
                              
                              {/* Year group - only show if Year type is included in group_types */}
                              {uiConfig.group_types && uiConfig.group_types.includes('Year') && (
                                <Grid item xs={12}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                    קבוצת דו"ח שנתי:
                                  </Typography>
                                  {client.YearGroup ? renderGroupChip(client.YearGroup) : 'לא הוגדר'}
                                </Grid>
                              )}
                              
                              {/* General groups - only show if General type is included in group_types */}
                              {uiConfig.group_types && uiConfig.group_types.includes('General') && (
                                <Grid item xs={12}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
                                    קבוצות כלליות:
                                  </Typography>
                                  {client.GeneralGroups && client.GeneralGroups.length > 0 ? (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                                      {client.GeneralGroups.map((genGroup) => renderGroupChip(genGroup))}
                                    </Box>
                                  ) : (
                                    'לא הוגדרו קבוצות כללי'
                                  )}
                                </Grid>
                              )}
                            </>
                          )}
                        </Grid>
                      </CardContent>
                      {editClientRoles.includes(role) && !isClientDeleted && (
                        <Box sx={{ p: { xs: 1.5, sm: 2 }, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Button
                            variant="contained"
                            color="primary"
                            sx={{ px: { xs: 2, sm: 3 }, py: { xs: 0.75, sm: 1 }, fontWeight: 'bold', borderRadius: 2, fontSize: { xs: '0.85rem !important', sm: 'inherit' } }}
                            onClick={handleOpenClientEdit}
                          >
                            עריכה
                          </Button>
                          <Button
                            variant="contained"
                            color="error"
                            sx={{ px: { xs: 2, sm: 3 }, py: { xs: 0.75, sm: 1 }, fontWeight: 'bold', borderRadius: 2, fontSize: { xs: '0.85rem !important', sm: 'inherit' } }}
                            onClick={handleOpenDeleteDialog}
                          >
                            מחיקה
                          </Button>
                        </Box>
                      )}
                    </Card>
                  </Grid>

                  {/* Right Card: Debt Summary - Dynamic fields based on config */}
                  <Grid item xs={12} md={6}>
                    <Card
                      sx={{
                        borderRadius: 2,
                        boxShadow: 3,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                      }}
                    >
                      <CardHeader
                        sx={{
                          backgroundColor: 'secondary.main',
                          color: 'white',
                          py: { xs: 1, sm: 1.5 },
                          px: { xs: 1.5, sm: 2 },
                        }}
                        title={
                          <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: { xs: '1rem !important', sm: '1.25rem !important' } }}>
                            סיכום חוב
                          </Typography>
                        }
                        action={
                          <Tooltip title="הדפסת יתרה">
                            <IconButton
                              onClick={handlePrintBalance}
                              size="small"
                              sx={{ color: 'white' }}
                              aria-label="הדפסת יתרה"
                            >
                              <PrintIcon />
                            </IconButton>
                          </Tooltip>
                        }
                      />
                      <CardContent sx={{ flexGrow: 1 }}>
                        {client.debt ? (
                          <>
                            { useTotalDebt && <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 1, mb: 2 }}>
                              <Typography variant="body1">
                                <strong>{debtFieldsSchema.TotalDebt?.label || 'סך חוב'}:</strong>{' '}
                                {new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(client.debt.TotalDebt)}
                              </Typography>
                            </Box> }
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                              פירוט חוב:
                            </Typography>
                            <Table size="small" sx={{ mb: 2 }}>
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary', width: '60%' }}>
                                    קטגוריה
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                                    סכום
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {/* Dynamically display debt fields based on config */}
                                {debtFields
                                  .filter(field => field !== 'TotalDebt')
                                  .map(field => (
                                    <TableRow key={field}>
                                      <TableCell>{debtFieldsSchema[field]?.label || field}</TableCell>
                                      <TableCell>
                                        {new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(
                                          client.debt.DebtFields[field] || 0
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  ))
                                }
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 'bold' }}>סה"כ</TableCell>
                                  <TableCell sx={{ fontWeight: 'bold' }}>
                                    {new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(
                                      // Calculate total from all debt fields
                                      debtFields
                                        .filter(field => field !== 'TotalDebt')
                                        .reduce((sum, field) => sum + parseFloat(client.debt.DebtFields[field] || 0), 0)
                                    )}
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </>
                        ) : (
                          <Typography variant="body1" sx={{ mt: 2 }}>
                            אין מידע על חוב זמין.
                          </Typography>
                        )}
                      </CardContent>
                      {editClientRoles.includes(role) && !isClientDeleted && (
                        <Box sx={{ p: 2, textAlign: 'start' }}>
                          <Button
                            variant="contained"
                            sx={{
                              backgroundColor: 'secondary.main',
                              color: 'white',
                              '&:hover': { backgroundColor: 'secondary.dark' },
                              px: 3,
                              py: 1,
                              fontWeight: 'bold',
                              borderRadius: 2,
                            }}
                            onClick={handleOpenDebtEdit}
                          >
                            עריכה
                          </Button>
                        </Box>
                      )}
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            </div>
            
            {/* =========================
                Client Comments Section
            ========================= */}
            <Divider sx={{ my: 4 }} />
            <Box sx={{ 
              maxWidth: { xs: '100%', md: '1200px' },
              mr: 0,
              ml: { xs: 0, md: 'auto' },
              mb: { xs: 2, sm: 4 }
            }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  הערות
              </Typography>
              <Card sx={{ borderRadius: 2, boxShadow: 3, mb: 4, p: 2, opacity: isClientDeleted ? 0.6 : 1 }}>
                  <Box>
                      <Typography
                          variant="body1"
                          sx={{ 
                              whiteSpace: 'pre-line', 
                              wordBreak: 'break-word',
                              maxHeight: '300px',
                              overflowY: 'auto',
                              fontSize: '1.5rem !important',
                          }}
                      >
                          {comments || 'אין הערות'}
                      </Typography>
                      <Box
                          sx={{
                              mt: 2,
                              display: 'flex',
                              justifyContent: 'flex-end',
                              gap: 1,
                          }}
                      >
                          <IconButton
                              size="large"
                              onClick={() => {
                                  setExpandedComments(true);
                                  setEditingComments(false);
                              }}
                              sx={{ color: 'blue' }}
                          >
                              <FullscreenIcon fontSize="large" />
                          </IconButton>

                          {editClientRoles.includes(role) && !isClientDeleted && (
                              <Button
                                  variant="contained"
                                  onClick={() => {
                                      setExpandedComments(true);
                                      setEditingComments(true);
                                  }}
                              >
                                  עריכה
                              </Button>
                          )}
                      </Box>
                  </Box>
              </Card>
            </Box>

            {/* =========================
                Expanded Comments Dialog
            ========================= */}
            <MobileDialog
                open={expandedComments}
                onClose={(event, reason) => {
                    // Prevent close on backdrop click if we're editing:
                    if (reason !== 'backdropClick' || !editingComments) {
                        setExpandedComments(false);
                        setEditingComments(false);
                    }
                }}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        height: { xs: '100%', sm: '75vh' },
                        maxWidth: { xs: '100%', sm: '80vw' },
                        display: 'flex',
                        flexDirection: 'column',
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        fontSize: { xs: '1.2rem', sm: '2rem' },
                        fontWeight: 'bold',
                        p: { xs: 1.5, sm: 2 },
                        flexShrink: 0,
                    }}
                >
                    הערות
                </DialogTitle>
                
                <DialogContent
                    dividers
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        position: 'relative',
                        p: 0,
                    }}
                >
                    {/* Main Content Container */}
                    <Box sx={{
                        flex: 1,
                        overflow: 'hidden',
                        padding: '16px',
                        paddingRight: '24px',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        {!editingComments ? (
                            <Box sx={{
                                flex: 1,
                                overflow: 'auto',
                            }}>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        whiteSpace: 'pre-line',
                                        wordBreak: 'break-word',
                                        fontSize: '2rem',
                                        lineHeight: '1.5',
                                    }}
                                >
                                    {comments || 'אין הערות'}
                                </Typography>
                            </Box>) : (
                            <Box sx={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'auto'
                            }}>
                                <TextField
                                    label="ערוך הערות"
                                    multiline
                                    fullWidth
                                    value={comments}
                                    onChange={(e) => setComments(e.target.value)}
                                    variant="outlined"
                                    inputRef={textFieldRef}
                                    sx={{
                                        flex: 1,
                                        '& .MuiInputBase-root': {
                                            fontSize: '2rem',
                                            height: '100%',
                                            alignItems: 'flex-start',
                                            overflow: 'auto',
                                            paddingRight: '8px',
                                        },
                                        
                                    }}
                                    InputProps={{
                                        style: { 
                                            height: '100%',
                                            boxSizing: 'border-box',
                                        }
                                    }}
                                />
                                
                                {/* Error/Success Messages */}
                                <Box sx={{ mt: 2, flexShrink: 0 }}>
                                    {commentsError && <Alert severity="error">{commentsError}</Alert>}
                                    {commentsSuccess && <Alert severity="success">{commentsSuccess}</Alert>}
                                </Box>
                            </Box>
                        )}
                    </Box>

                    {/* Sticky Bottom Buttons */}
                    <Box sx={{
                        position: 'sticky',
                        bottom: 0,
                        bgcolor: 'background.paper',
                        p: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        zIndex: 2,
                        display: 'flex',
                        gap: 2,
                        justifyContent: 'flex-start',
                    }}>
                        {editingComments ? (
                            <>
                                <Button
                                    onClick={() => {
                                        setExpandedComments(false);
                                        setEditingComments(false);
                                        setCommentsError('');
                                        setCommentsSuccess('');
                                        setComments(client.ClientComments || '');
                                    }}
                                    variant="outlined"
                                    sx={{ fontSize: '1.5rem', minWidth: '120px' }}
                                >
                                    ביטול
                                </Button>
                                <Button
                                    onClick={handleSaveComments}
                                    variant="contained"
                                    disabled={commentsLoading}
                                    sx={{ fontSize: '1.5rem', minWidth: '120px' }}
                                >
                                    {commentsLoading ? 'שומר...' : 'שמור'}
                                </Button>
                            </>
                        ) : (
                            <>
                                <IconButton
                                    onClick={() => setExpandedComments(false)}
                                    sx={{ color: 'blue' }}
                                >
                                    <FullscreenExitIcon fontSize="large" />
                                </IconButton>
                                {editClientRoles.includes(role) && !isClientDeleted && (
                                    <Button
                                        variant="contained"
                                        onClick={() => setEditingComments(true)}
                                        sx={{ fontSize: '1.5rem', minWidth: '120px' }}
                                    >
                                        עריכה
                                    </Button>
                                )}
                            </>
                        )}
                    </Box>
                </DialogContent>
            </MobileDialog>
            {/* =========================
                Orders Section - FULL WIDTH with DataGridPro
            ========================= */}
            {includeOrders && (
              <>
                <Divider sx={{ my: 4 }} />

                {/* Section Header with Title and Action Buttons */}
                <Box
                  ref={ordersTableRef}
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'stretch', sm: 'center' },
                    gap: 1,
                    mb: 2
                  }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {orderPluralLabel}
                  </Typography>

                  {!isClientDeleted && (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<CalendarMonthIcon />}
                        onClick={() => setOrdersMonthlyStatementDialogOpen(true)}
                        sx={{ direction: 'ltr', flex: { xs: 1, sm: 'none' } }}
                      >
                        דוח חודשי
                      </Button>

                      <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<SummarizeIcon />}
                        onClick={() => setExportOrderItemsDialogOpen(true)}
                        sx={{
                          borderColor: 'primary.main',
                          direction: 'ltr',
                          flex: { xs: 1, sm: 'none' },
                          '&:hover': {
                            backgroundColor: 'primary.light',
                            borderColor: 'primary.dark',
                          }
                        }}
                      >
                        סיכום {productPluralLabel}
                      </Button>

                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => navigate(`/order/new?clientId=${business_id}`)}
                        sx={{ direction: 'ltr', flex: { xs: 1, sm: 'none' } }}
                      >
                        {newOrderLabel}
                      </Button>
                    </Box>
                  )}
                </Box>
                
                <Card sx={{ borderRadius: 2, boxShadow: 3, mb: 4, width: '100%', opacity: isClientDeleted ? 0.6 : 1 }}>
                  <CardHeader
                    sx={{
                      backgroundColor: 'primary.main',
                      color: 'white',
                      py: 1.5,
                    }}
                    title={
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        רשימת {orderPluralLabel} {loadingOrders && <CircularProgress size={20} sx={{ ml: 1 }} />}
                      </Typography>
                    }
                  />
                  <CardContent>
                    {/* Filters Row */}
                    <ResponsiveToolbar sx={{ mb: 2 }}>
                      {orderFields.includes("Status") &&
                      <TextField
                        select
                        label="סטטוס"
                        size="small"
                        value={orderFilters.Status}
                        onChange={(e) => handleOrderFilterChange('Status', e.target.value)}
                        sx={{ width: { xs: '100%', md: 140 }, minWidth: { xs: 0, md: 140 } }}
                      >
                        <MenuItem value="">הכל</MenuItem>
                        {(orderFieldsSchema?.Status?.options || [
                          { value: 'pending', label: 'בתהליך' },
                          { value: 'processing', label: 'בטיפול' },
                          { value: 'completed', label: 'הושלם' },
                          { value: 'cancelled', label: 'בוטל' },
                        ]).map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                      }
                      <Autocomplete
                        key={orderAutocompleteKey}
                        options={availableOrders}
                        getOptionLabel={(option) => {
                          // Handle both object and string cases
                          if (typeof option === 'object' && option !== null) {
                            return option.number || '';
                          }
                          return option || '';
                        }}
                        getOptionDisabled={(option) => option.isNoResults === true}
                        value={orderNumberInput ? { number: orderNumberInput } : null}
                        onChange={(event, newValue) => {
                          // Ignore dummy "no results" record
                          if (newValue && typeof newValue === 'object' && newValue.isNoResults) {
                            return;
                          }

                          if (newValue) {
                            // Reset pagination immediately when selecting an order
                            setOrderPaginationModel(prev => ({ ...prev, page: 0 }));

                            // Cancel any pending autocomplete search
                            if (orderAutocompleteTimerCleanup.current) {
                              orderAutocompleteTimerCleanup.current();
                            }
                            setDebouncedOrderSearch('');  // Stop any pending/future searches

                            // If it's an object with id property, use that for filtering
                            if (typeof newValue === 'object' && newValue !== null && newValue.id) {
                              setOrderNumberInput(newValue.number);
                              orderNumberInputRef.current = newValue.number;
                              setOrderIdForFiltering(newValue.id.toString());
                            } else {
                              // Handle free text entry
                              setOrderNumberInput(newValue);
                              orderNumberInputRef.current = newValue;
                              setOrderIdForFiltering(''); // Clear ID since we don't know it
                            }

                            // Clear all other filters when selecting an order
                            setOrderFilters(prev => ({
                              ...prev,
                              Status: '',
                              startDate: null,
                              endDate: null,
                            }));
                          } else {
                            // Handle clearing - restore default date filtering
                            setOrderNumberInput('');
                            orderNumberInputRef.current = '';
                            setOrderIdForFiltering('');
                            setDebouncedOrderSearch('');

                            // Clear any pending timer
                            if (orderAutocompleteTimerCleanup.current) {
                              orderAutocompleteTimerCleanup.current();
                            }

                            // Restore default date filters (null = show all orders)
                            setOrderFilters(prev => ({
                              ...prev,
                              startDate: null,
                              endDate: null,
                            }));
                          }
                        }}
                        onInputChange={(event, newInputValue, reason) => {
                          // Update ref only (doesn't trigger re-render) - this fixes the lag
                          orderNumberInputRef.current = newInputValue;

                          // Only trigger search when user is actually typing (not on selection or reset)
                          if (reason === 'input') {
                            // Clear previous timer
                            if (orderAutocompleteTimerCleanup.current) {
                              orderAutocompleteTimerCleanup.current();
                            }

                            // Debounce the API call for autocomplete
                            orderAutocompleteTimerCleanup.current = debouncedSetOrderSearch(newInputValue);
                          }

                          // Handle clearing the field (via clear button or backspace to empty)
                          if (reason === 'clear' || (reason === 'input' && newInputValue === '')) {
                            setOrderNumberInput('');
                            setOrderIdForFiltering('');
                            setDebouncedOrderSearch('');
                            // Clear any pending timer
                            if (orderAutocompleteTimerCleanup.current) {
                              orderAutocompleteTimerCleanup.current();
                            }
                            setOrderFilters(prev => ({
                              ...prev,
                              startDate: null,
                              endDate: null,
                            }));
                          }
                        }}
                        loading={isLoadingOrderAutocomplete}
                        loadingText="טוען..."
                        filterOptions={(x) => x}  // Disable client-side filtering (we do server-side)
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={orderFieldsSchema?.OrderNumber?.label || `מספר ${orderLabel}`}
                            size="small"
                            placeholder={`בחר מספר ${orderLabel}`}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {isLoadingOrderAutocomplete ? <CircularProgress color="inherit" size={20} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        freeSolo
                        sx={{ width: { xs: '100%', md: 200 }, minWidth: { xs: 0, md: 200 } }}
                      />

                      {/* Date Range Filters */}
                      <DatePicker
                        label="חודש"
                        value={orderFilters.startDate}
                        onChange={(newDate) => {
                          if (newDate) {
                            handleOrderFilterChange('startDate', startOfMonth(newDate));
                            handleOrderFilterChange('endDate', endOfMonth(newDate));
                          }
                        }}
                        views={['month', 'year']}
                        slotProps={{
                          textField: {
                            size: 'small',
                            sx: { direction: 'ltr', width: { xs: '100%', md: 180 }, minWidth: { xs: 0, md: 180 } }
                          },
                        }}
                      />

                      <DatePicker
                        label="מתאריך"
                        value={orderFilters.startDate}
                        onChange={(newDate) => handleOrderFilterChange('startDate', newDate)}
                        slotProps={{
                          textField: { size: 'small', sx: { direction: 'ltr', width: { xs: '100%', md: 200 }, minWidth: { xs: 0, md: 200 } } },
                          field: { clearable: true }
                        }}
                      />

                      <DatePicker
                        label="עד תאריך"
                        value={orderFilters.endDate}
                        onChange={(newDate) => handleOrderFilterChange('endDate', newDate)}
                        slotProps={{
                          textField: { size: 'small', sx: { direction: 'ltr', width: { xs: '100%', md: 200 }, minWidth: { xs: 0, md: 200 } } },
                          field: { clearable: true }
                        }}
                      />

                      <Button
                        variant="outlined"
                        color="inherit"
                        size="small"
                        onClick={resetAndFetchOrders}
                        disabled={!hasAppliedFilters()}
                      >
                        נקה מסננים
                      </Button>

                    </ResponsiveToolbar>

                    {/* Orders DataGridPro with expandable detail panel */}
                    <Box sx={{
                      width: '100%',
                      '.MuiDataGrid-columnHeaderTitle': {
                        fontWeight: 'bold',
                        fontSize: '1.15rem',
                        whiteSpace: 'normal !important',
                        lineHeight: '1.2',
                        overflow: 'visible',
                      },
                      '.MuiDataGrid-cell': {
                        fontSize: '1rem',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word'
                      },
                    }}>
                      <ResponsiveDataView
                        rows={clientOrders}
                        columns={FinalColumns}
                        getRowId={(row) => row.OrderID}
                        cardRenderer={orderCardRenderer}
                        onRowClick={(row) => navigate(`/order/${row.OrderID}`, { state: { from: `/client/${business_id}` } })}
                        emptyMessage="אין תוצאות"
                        density="standard"

                        // Server-side pagination
                        paginationMode="server"
                        paginationModel={orderPaginationModel}
                        onPaginationModelChange={handleOrderPaginationModelChange}
                        rowCount={orderTotalRows}
                        pageSizeOptions={[25, 50, 100]}
                        pagination

                        // Server-side sorting
                        sortingMode="server"
                        onSortModelChange={handleOrderSortModelChange}

                        disableSelectionOnClick
                        loading={loadingOrders}
                          localeText={heIL.components.MuiDataGrid.defaultProps.localeText}
                          slots={{
                            toolbar: () =>
                              <OrdersToolbar
                                onRefresh={fetchClientOrders}
                              />
                          }}
                          // Detail panel configuration
                          getDetailPanelContent={({ row }) => (
                            <OrderDetailPanel
                              orderId={row.OrderID}
                              generalSchema={generalSchema}
                              orderItemFields={orderItemFields}
                              orderItemFieldsSchema={orderItemFieldsSchema}
                            />
                          )}
                          getDetailPanelHeight={() => 'auto'}
                          // No-rows overlay
                          components={{
                            NoRowsOverlay: () => (
                              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', py: 5 }}>
                                <Typography color="text.secondary">אין {orderPluralLabel} להצגה</Typography>
                              </Box>
                            ),
                          }}
                          initialState={{
                            sorting: {
                              sortModel: [{ field: 'OrderDate', sort: 'desc' }],
                            },
                          }}
                          getRowClassName={(params) => {
                            if (params.row.Status === 'cancelled') return 'cancelled-row';
                            return '';
                          }}
                          autoHeight
                          sx={{
                            '& .cancelled-row': {
                              color: 'text.disabled',
                              bgcolor: 'rgba(0, 0, 0, 0.04)',
                            },
                            '& .MuiDataGrid-footerContainer': {
                              borderTop: '1px solid rgba(224, 224, 224, 1)',
                            },
                          }}
                        />
                    </Box>
                  </CardContent>
                </Card>
              </>
            )}
{/* Order Summary */}
{includeOrders && (
<Box sx={{ mt: 3, mb: 2 }}>
  {/* Summary Title */}
  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, textAlign: 'right' }}>
    סיכום {orderPluralLabel}:
  </Typography>

  {/* Warning message when no date range is set */}
  {!((orderFilters.startDate instanceof Date && !isNaN(orderFilters.startDate)) ||
     (orderFilters.endDate instanceof Date && !isNaN(orderFilters.endDate))) && (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
      <WarningAmberIcon sx={{ color: 'warning.main', fontSize: '1.2rem' }} />
      <Typography variant="body2" color="text.secondary">
        הגדר טווח תאריכים לצפייה בסיכום מדויק
      </Typography>
    </Box>
  )}

  {/* Summary Items */}
  <Box sx={{
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: 3,
    flexWrap: 'wrap'
  }}>
    {/* Total Orders Count */}
    <Box sx={{
      backgroundColor: '#e3f2fd', // Light blue
      padding: '8px 16px',
      borderRadius: 2,
      border: '1px solid #90caf9',
      display: 'inline-block'
    }}>
      <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 'medium' }}>
        מספר {orderPluralLabel}:{' '}
        <Typography component="span" sx={{ fontWeight: 'bold' }}>
          {((orderFilters.startDate instanceof Date && !isNaN(orderFilters.startDate)) ||
            (orderFilters.endDate instanceof Date && !isNaN(orderFilters.endDate)))
            ? (orderSummary?.totalOrders || 0)
            : 'אין מידע'}
        </Typography>
      </Typography>
    </Box>

    {/* Total Amount */}
    <Box sx={{
      backgroundColor: '#f3e5f5', // Light purple
      padding: '8px 16px',
      borderRadius: 2,
      border: '1px solid #ce93d8',
      display: 'inline-block'
    }}>
      <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 'medium' }}>
        סכום כולל:{' '}
        <Typography component="span" sx={{ fontWeight: 'bold' }}>
          {((orderFilters.startDate instanceof Date && !isNaN(orderFilters.startDate)) ||
            (orderFilters.endDate instanceof Date && !isNaN(orderFilters.endDate)))
            ? new Intl.NumberFormat('he-IL', {
                style: 'currency',
                currency: 'ILS',
              }).format(orderSummary?.totalAmount || 0)
            : 'אין מידע'}
        </Typography>
      </Typography>
    </Box>
  </Box>
</Box>
)}
            {/* =========================
                Transactions Section - FULL WIDTH
            ========================= */}
            <Divider sx={{ my: 4 }} />
            {/* No Box container with maxWidth here - keeps full width */}
            <Typography ref={transactionsTableRef} variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
              היסטוריית תנועות
            </Typography>

            <Card sx={{ borderRadius: 2, boxShadow: 3, mb: 4, width: '100%' }}>
              <CardHeader
                sx={{
                  backgroundColor: 'primary.main',
                  color: 'white',
                  py: 1.5,
                }}
                title={
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    תנועות
                  </Typography>
                }
              />
              <CardContent>
                {/* Filter Controls + Print Button */}
                <ResponsiveToolbar sx={{ mb: 2 }}>
                  <DatePicker
                    label="חודש"
                    value={startDate}
                    onChange={(newValue) => {
                      if (newValue) {
                        setStartDate(startOfMonth(newValue));
                        setEndDate(endOfMonth(newValue));
                      }
                    }}
                    views={['month', 'year']}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: { direction: 'ltr', width: { xs: '100%', md: 150 }, minWidth: { xs: 0, md: 150 } },
                      },
                    }}
                  />

                  <DatePicker
                    label="מתאריך"
                    value={startDate}
                    onChange={(newValue) => setStartDate(newValue)}
                    format="dd/MM/yyyy"
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: { direction: 'ltr', width: { xs: '100%', md: 160 }, minWidth: { xs: 0, md: 160 } },
                      },
                      field: { clearable: true }
                    }}
                  />

                  <DatePicker
                    label="עד תאריך"
                    value={endDate}
                    onChange={(newValue) => setEndDate(newValue)}
                    format="dd/MM/yyyy"
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: { direction: 'ltr', width: { xs: '100%', md: 160 }, minWidth: { xs: 0, md: 160 } },
                      },
                      field: { clearable: true }
                    }}
                  />

                  <TextField
                    select
                    label="סוג תנועה"
                    size="small"
                    value={transactionType}
                    onChange={(e) => setTransactionType(e.target.value)}
                    sx={{ width: { xs: '100%', md: 180 }, minWidth: { xs: 0, md: 180 } }}
                  >
                    <MenuItem value="All">הכל</MenuItem>
                    <MenuItem value="Payment">תשלום</MenuItem>
                    <MenuItem value="Charge">חיוב</MenuItem>
                    <MenuItem value="Credit">זיכוי</MenuItem>
                  </TextField>

                  {debtFields.length > 1 && (
                    <TextField
                      select
                      label="סוג חוב"
                      size="small"
                      value={debtTypeFilter}
                      onChange={(e) => setDebtTypeFilter(e.target.value)}
                      sx={{ width: { xs: '100%', md: 180 }, minWidth: { xs: 0, md: 180 } }}
                    >
                      <MenuItem value="All">הכל</MenuItem>
                      {debtFields.map((field) => (
                        <MenuItem key={field} value={field}>
                          {debtFieldsSchema[field]?.label || field}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}

                  {transactionFields.includes('OrderNumber') && (
                    <Autocomplete
                      size="small"
                      options={availableOrderNumbers}
                      value={orderNumberFilter}
                      onChange={(e, newValue) => setOrderNumberFilter(newValue)}
                      sx={{ width: { xs: '100%', md: 200 }, minWidth: { xs: 0, md: 200 } }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={transactionFieldsSchema.OrderNumber?.label || `מספר ${orderLabel}`}
                          size="small"
                        />
                      )}
                    />
                  )}

                  <ToolbarActions>
                    <Button
                      variant="outlined"
                      color="inherit"
                      size="small"
                      onClick={handleResetTransactionFilters}
                      disabled={!hasAppliedTransactionFilters()}
                    >
                      נקה מסננים
                    </Button>
                    <Tooltip
                      title={!((startDate instanceof Date && !isNaN(startDate)) || (endDate instanceof Date && !isNaN(endDate)))
                        ? "הגדר טווח תאריכים להדפסה"
                        : ""}
                    >
                      <span style={{ display: 'flex', flex: 1 }}>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          fullWidth
                          onClick={handlePrintPage}
                          disabled={printLoading || !((startDate instanceof Date && !isNaN(startDate)) || (endDate instanceof Date && !isNaN(endDate)))}
                        >
                          {printLoading ? 'טוען...' : 'הדפסה'}
                        </Button>
                      </span>
                    </Tooltip>
                    <Button
                      variant="outlined"
                      color="secondary"
                      size="small"
                      onClick={() => setMonthlyStatementDialogOpen(true)}
                    >
                      דוח חודשי
                    </Button>
                  </ToolbarActions>
                </ResponsiveToolbar>

                  <Box sx={{ width: '100%', overflowX: 'auto' }}>
                    <ResponsiveDataView
                      rows={transactions}
                      columns={transactionColumns}
                      getRowId={(row) => row.TrID}
                      cardRenderer={transactionCardRenderer}
                      onRowClick={(row) => setMobileTransactionDetail(row)}
                      emptyMessage="אין תוצאות"
                      autoHeight
                      pagination
                      paginationMode="server"
                      rowCount={transactionRowCount}
                      loading={loadingTransactions}
                      paginationModel={{ page: transactionPageNumber, pageSize: transactionPageSize }}
                      onPaginationModelChange={handleTransactionPaginationModelChange}
                      pageSizeOptions={[25, 50, 100]}
                      sortModel={transactionSortModel}
                      onSortModelChange={handleTransactionSortModelChange}
                      disableSelectionOnClick
                      localeText={heIL.components.MuiDataGrid.defaultProps.localeText}
                      components={{
                        Toolbar: CustomToolbar,
                        NoRowsOverlay: () => (
                          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            אין תנועות להצגה
                          </Box>
                        ),
                      }}
                      // Detail panel configuration
                      getDetailPanelContent={({ row }) => (
                        <TransactionDetailPanel
                          transaction={row}
                          transactionFields={transactionFields}
                          transactionFieldsSchema={transactionFieldsSchema}
                          debtFieldsSchema={debtFieldsSchema}
                          clientFieldsSchema={clientFieldsSchema}
                          orderTransactionFieldsSchema={orderTransactionFieldsSchema}
                          onEdit={(transaction) => {
                            setEditingTransaction(transaction);
                            setEditTransactionDialogOpen(true);
                          }}
                          onDelete={(transaction) => {
                            setDeletingTransaction(transaction);
                            setDeleteTransactionDialogOpen(true);
                            setTransactionDeletePassword('');
                            setTransactionDeleteError('');
                          }}
                          userRole={role}
                        />
                      )}
                      getDetailPanelHeight={() => 'auto'}
                      getRowClassName={(params) => {
                        const type = params.row?.Type;
                        switch (type) {
                          case 'Payment':
                            return 'payment-row';
                          case 'Charge':
                            return 'charge-row';
                          case 'Credit':
                            return 'credit-row';
                          default:
                            return '';
                        }
                      }}
                      sx={{
                      '.MuiDataGrid-columnHeaderTitle': { fontWeight: 'bold', fontSize: '1.15rem',
                          whiteSpace: 'normal !important',  // ADD THIS - allows header text to wrap
                          lineHeight: '1.2',  
                          overflow: 'visible',  
                        },
                        '.MuiDataGrid-cell': { fontSize: '1rem', whiteSpace: 'normal', wordBreak: 'break-word' },
                        // ADD THESE CSS CLASSES:
                        '& .payment-row': {
                          backgroundColor: '#e8f5e9', // Light green
                          '&:hover': {
                            backgroundColor: '#c8e6c9', // Slightly darker green on hover
                          },
                        },
                        '& .charge-row': {
                          backgroundColor: '#ffebee', // Light red/pink
                          '&:hover': {
                            backgroundColor: '#ffcdd2', // Slightly darker red on hover
                          },
                        },
                        '& .credit-row': {
                          backgroundColor: '#fff8e1', // Light yellow
                          '&:hover': {
                            backgroundColor: '#ffecb3', // Slightly darker yellow on hover
                          },
                        },
                      }}
                    />
                    
                    {/* Order filter summary (fetched from backend) */}
                    {orderSummaryData && (
                      <Paper
                        elevation={2}
                        sx={{
                          p: 2,
                          mt: 2,
                          borderRadius: 2,
                          backgroundColor: '#f8f9fa'
                        }}
                      >
                        <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
                          סיכום ל{orderLabel}: {orderSummaryData.orderNumber}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                          <Typography>
                            תשלומים: {new Intl.NumberFormat('he-IL', {
                              style: 'currency',
                              currency: 'ILS'
                            }).format(orderSummaryData.payment_total)}
                          </Typography>
                          <Typography>
                            חיובים: {new Intl.NumberFormat('he-IL', {
                              style: 'currency',
                              currency: 'ILS'
                            }).format(orderSummaryData.charge_total)}
                          </Typography>
                          <Typography>
                            זיכויים: {new Intl.NumberFormat('he-IL', {
                              style: 'currency',
                              currency: 'ILS'
                            }).format(orderSummaryData.credit_total)}
                          </Typography>
                          <Typography sx={{ fontWeight: 'bold' }}>
                            יתרה: {new Intl.NumberFormat('he-IL', {
                              style: 'currency',
                              currency: 'ILS'
                            }).format(orderSummaryData.charge_total - orderSummaryData.payment_total - orderSummaryData.credit_total)}
                          </Typography>
                        </Box>
                      </Paper>
                    )}
                  </Box>
              </CardContent>
            </Card>

            {/* =========================
                Debt Edit Dialog - Dynamic fields based on config
            ========================= */}
            {/* =============== Transaction Summary Section =============== */}
            {summaryAllowedRoles.includes(role) && (
              <Box sx={{ mt: 2 }}>
                {/* Small warning message above chips when no date range is set */}
                {!((startDate instanceof Date && !isNaN(startDate)) || (endDate instanceof Date && !isNaN(endDate))) && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
                    <WarningAmberIcon sx={{ color: 'warning.main', fontSize: '1.2rem' }} />
                    <Typography variant="body2" color="text.secondary">
                      הגדר טווח תאריכים לצפייה בסיכום מדויק
                    </Typography>
                  </Box>
                )}

                {/* Summary Title */}
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, textAlign: 'right' }}>
                  סיכום סינון נוכחי:
                </Typography>

                {/* Summary Items - Always show, with "אין מידע" when no date range */}
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  gap: 3,
                  flexWrap: 'wrap'
                }}>
                  {/* Payment Summary */}
                  <Box sx={{
                    backgroundColor: '#e8f5e9',
                    padding: '8px 16px',
                    borderRadius: 2,
                    border: '1px solid #c8e6c9',
                    display: 'inline-block'
                  }}>
                    <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 'medium' }}>
                      סך תשלומים:{' '}
                      <Typography component="span" sx={{ fontWeight: 'bold' }}>
                        {((startDate instanceof Date && !isNaN(startDate)) || (endDate instanceof Date && !isNaN(endDate)))
                          ? new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(summaryData.payment_total)
                          : 'אין מידע'}
                      </Typography>
                    </Typography>
                  </Box>

                  {/* Charge Summary */}
                  <Box sx={{
                    backgroundColor: '#ffebee',
                    padding: '8px 16px',
                    borderRadius: 2,
                    border: '1px solid #ffcdd2',
                    display: 'inline-block'
                  }}>
                    <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 'medium' }}>
                      סך חיובים:{' '}
                      <Typography component="span" sx={{ fontWeight: 'bold' }}>
                        {((startDate instanceof Date && !isNaN(startDate)) || (endDate instanceof Date && !isNaN(endDate)))
                          ? new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(summaryData.charge_total)
                          : 'אין מידע'}
                      </Typography>
                    </Typography>
                  </Box>

                  {/* Credit Summary */}
                  <Box sx={{
                    backgroundColor: '#fff8e1',
                    padding: '8px 16px',
                    borderRadius: 2,
                    border: '1px solid #ffecb3',
                    display: 'inline-block'
                  }}>
                    <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 'medium' }}>
                      סך זיכויים:{' '}
                      <Typography component="span" sx={{ fontWeight: 'bold' }}>
                        {((startDate instanceof Date && !isNaN(startDate)) || (endDate instanceof Date && !isNaN(endDate)))
                          ? new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(summaryData.credit_total)
                          : 'אין מידע'}
                      </Typography>
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            <MobileDialog
              open={editDialogOpen}
              onClose={(event, reason) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
                handleCancel();
              }}
              maxWidth="lg"
              fullWidth
              PaperProps={{ sx: { fontSize: { xs: '0.95rem', sm: '1.2rem' }, p: { xs: 1, sm: 2 } } }}
            >
              <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                עריכת חוב עבור: <strong>{client.ClientName}</strong>
              </DialogTitle>
              {!confirmStep ? (
                <DialogContent dividers>
                  {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}
                  {successMsg && <Alert severity="success" sx={{ mb: 2 }}>{successMsg}</Alert>}
                  <Typography variant="body1" sx={{ mb: 2, fontSize: '1.2rem' }}>
                    אנא הזן ערכי יעד חדשים עבור קטגוריות החוב.
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {/* Dynamically generate debt field inputs based on config */}
                    {debtFields
                      .filter(field => field !== 'TotalDebt')
                      .map(field => (
                        <TextField
                          key={field}
                          label={debtFieldsSchema[field]?.label || field}
                          type="number"
                          value={debtFieldValues[field]}
                          onChange={(e) => handleDebtFieldChange(field, e.target.value)}
                          onFocus={(e) => e.target.select()}
                          InputProps={{ sx: { fontSize: '1.2rem' } }}
                          InputLabelProps={{ sx: { fontSize: '1.2rem' } }}
                          sx={{
                            width: '48%',
                            direction: 'ltr',
                            '& input': {
                              textAlign: 'right',
                              '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                                WebkitAppearance: 'none',
                                margin: 0,
                              },
                              '-moz-appearance': 'textfield',
                            },
                          }}
                          margin="normal"
                        />
                      ))
                    }
                    <TextField
                      label="הערה (אופציונלי)"
                      type="text"
                      value={userNote}
                      onChange={(e) => setUserNote(e.target.value)}
                      InputProps={{ sx: { fontSize: '1.2rem' } }}
                      InputLabelProps={{ sx: { fontSize: '1.2rem' } }}
                      sx={{ width: '48%' }}
                      margin="normal"
                    />
                  </Box>
                </DialogContent>
              ) : (
                <DialogContent dividers>
                  {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}
                  {successMsg && <Alert severity="success" sx={{ mb: 2 }}>{successMsg}</Alert>}
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', fontSize: '1.3rem' }}>
                    אישור שינויים
                  </Typography>
                  <Typography sx={{ mb: 1, fontSize: '1.2rem' }}>
                    להלן הערכים החדשים שיוגדרו עבור <strong>{client.ClientName}</strong>:
                  </Typography>
                  <ul style={{ marginTop: 0, fontSize: '1.2rem' }}>
                    {/* Dynamically list all debt field values for confirmation */}
                    {debtFields
                      .filter(field => field !== 'TotalDebt')
                      .map(field => (
                        <li key={field}>
                          {debtFieldsSchema[field]?.label || field}: {debtFieldValues[field] || 0}
                        </li>
                      ))
                    }
                  </ul>
                  <Typography sx={{ mt: 2, mb: 2, fontSize: '1.2rem' }}>
                    <strong>הערה שלך:</strong> {userNote || 'ללא הערה'}
                  </Typography>
                  <Typography color="warning.main" sx={{ fontSize: '1.2rem' }}>
                    האם לאשר ולבצע את העדכון הסופי?
                  </Typography>
                </DialogContent>
              )}

              {/* Hide the action buttons if successMsg is shown */}
              {!successMsg && (
                <DialogActions>
                  {!confirmStep ? (
                    <>
                      <Button onClick={handleCancel} color="inherit" sx={{ fontSize: '1rem' }}>
                        ביטול
                      </Button>
                      <Button
                        onClick={handleSaveUpdates}
                        variant="contained"
                        color="primary"
                        sx={{ fontSize: '1rem' }}
                      >
                        שמירת עדכונים
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        onClick={handleGoBack}
                        color="inherit"
                        sx={{ fontSize: '1rem' }}
                        disabled={isUpdatingDebt}
                      >
                        חזרה
                      </Button>
                      <Button
                        onClick={handleConfirmUpdates}
                        variant="contained"
                        color="primary"
                        sx={{ fontSize: '1rem' }}
                        ref={confirmDebtButtonRef}
                        disabled={isUpdatingDebt}
                      >
                        אישור סופי
                      </Button>
                    </>
                  )}
                </DialogActions>
              )}
            </MobileDialog>

            {/* =========================
                Client Edit Dialog - Dynamic fields based on config
            ========================= */}
            <MobileDialog
              open={clientEditDialogOpen}
              onClose={(event, reason) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
                handleCancelClient();
              }}
              maxWidth="lg"
              fullWidth
              PaperProps={{ sx: { fontSize: { xs: '0.95rem', sm: '1.2rem' }, p: { xs: 1, sm: 2 } } }}
            >
              <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                עריכת פרטי לקוח: <strong>{client.ClientName}</strong>
              </DialogTitle>
              {!clientConfirmStep ? (
                <DialogContent dividers>
                  {clientErrorMsg && <Alert severity="error" sx={{ mb: 2 }}>{clientErrorMsg}</Alert>}
                  {clientSuccessMsg && <Alert severity="success" sx={{ mb: 2 }}>{clientSuccessMsg}</Alert>}
                  <Typography variant="body1" sx={{ mb: 2, fontSize: '1.2rem' }}>
                    אנא עדכן/י את הפרטים הרלוונטיים ללקוח זה.
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {/* Dynamically generate client field inputs based on config */}
                    {clientFields
                      .filter(field => {
                        const schema = clientFieldsSchema[field];
                        return schema &&
                              schema.readonly === false &&
                              field !== 'IsDeleted' &&
                              field !== 'DeletedAt' &&
                              field !== 'CreatedAt' &&
                              field !== 'ClientComments' &&
                              field !== 'FlowGroup' &&        // Exclude FlowGroup
                              field !== 'YearGroup' &&        // Exclude YearGroup
                              field !== 'GeneralGroups' &&
                              !(field === 'ClientBusinessID' && autoGenerateId); // Readonly when auto-generated
                      })
                      .map(field => {
                        const schema = clientFieldsSchema[field];
                        const isNumber = schema.type === 'number';
                        const isDate = schema.type === 'date';
                        const isBusinessID = field === 'ClientBusinessID';

                        // Render DatePicker for date-type fields
                        if (isDate) {
                          const labelText = schema.required
                            ? (schema.label || field)
                            : `${schema.label || field} (אופציונלי)`;
                          return (
                            <LocalizationProvider
                              dateAdapter={AdapterDateFns}
                              adapterLocale={heLocale}
                              key={field}
                              localeText={{
                                cancelButtonLabel: 'ביטול',
                                okButtonLabel: 'אישור',
                                clearButtonLabel: 'נקה',
                                todayButtonLabel: 'היום',
                              }}
                            >
                              <DatePicker
                                label={labelText}
                                value={clientFieldValues[field] ? new Date(clientFieldValues[field]) : null}
                                onChange={(newValue) => {
                                  // Store as local YYYY-MM-DD for API (not toISOString,
                                  // which shifts to UTC and yields the previous day for Israel users).
                                  const dateStr = newValue
                                    ? `${newValue.getFullYear()}-${String(newValue.getMonth() + 1).padStart(2, '0')}-${String(newValue.getDate()).padStart(2, '0')}`
                                    : null;
                                  handleClientFieldChange(field, dateStr);
                                }}
                                disableFuture={field === 'BirthDate'}
                                maxDate={field === 'BirthDate' ? new Date() : undefined}
                                sx={{ width: '48%', direction: 'ltr', mt: 2 }}
                                slotProps={{
                                  textField: {
                                    required: schema.required,
                                    InputProps: { sx: { fontSize: '1.2rem' } },
                                    InputLabelProps: { sx: { fontSize: '1.2rem' } },
                                  },
                                }}
                                format="dd/MM/yyyy"
                              />
                            </LocalizationProvider>
                          );
                        }

                        return (
                          <TextField
                            key={field}
                            label={schema.label || field}
                            type={isNumber ? "number" : "text"}
                            value={clientFieldValues[field] || ''}
                            onChange={(e) => handleClientFieldChange(field, e.target.value)}
                            InputProps={{ sx: { fontSize: '1.2rem' } }}
                            InputLabelProps={{ sx: { fontSize: '1.2rem' } }}
                            required={schema.required}
                            sx={{
                              width: '48%',
                              direction: isNumber || isBusinessID ? 'ltr' : 'inherit',
                              '& input': isNumber || isBusinessID ? {
                                textAlign: 'right',
                                '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                                  WebkitAppearance: 'none',
                                  margin: 0,
                                },
                                '-moz-appearance': 'textfield',
                              } : {}
                            }}
                            margin="normal"
                          />
                        );
                      })
                    }
                  </Box>
                </DialogContent>
              ) : (
                <DialogContent dividers>
                  {clientErrorMsg && <Alert severity="error" sx={{ mb: 2 }}>{clientErrorMsg}</Alert>}
                  {clientSuccessMsg && <Alert severity="success" sx={{ mb: 2 }}>{clientSuccessMsg}</Alert>}
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', fontSize: '1.3rem' }}>
                    אישור שינויים
                  </Typography>
                  <Typography sx={{ mb: 1, fontSize: '1.2rem' }}>
                    להלן הפרטים החדשים שיוגדרו עבור <strong>{client.ClientName}</strong>:
                  </Typography>
                  <ul style={{ marginTop: 0, fontSize: '1.2rem' }}>
                    {/* Dynamically list all client field values for confirmation */}
                    {Object.entries(clientFieldValues).map(([field, value]) => {
                      const schema = clientFieldsSchema[field];
                      if (!schema || field === 'IsDeleted' || field === 'DeletedAt' || field === 'CreatedAt') return null;

                      // Format date fields for display
                      let displayValue = value;
                      if (schema.type === 'date' && value) {
                        displayValue = new Date(value).toLocaleDateString('he-IL', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                        });
                      }

                      return (
                        <li key={field}>
                          {schema.label || field}: {displayValue || ''}
                        </li>
                      );
                    })}
                  </ul>
                  <Typography color="warning.main" sx={{ fontSize: '1.2rem', mt: 2 }}>
                    האם לאשר ולבצע את העדכון הסופי?
                  </Typography>
                </DialogContent>
              )}

              {/* Hide the action buttons if successMsg is shown */}
              {!clientSuccessMsg && (
                <DialogActions>
                  {!clientConfirmStep ? (
                    <>
                      <Button onClick={handleCancelClient} color="inherit" sx={{ fontSize: '1rem' }}>
                        ביטול
                      </Button>
                      <Button
                        onClick={handleSaveClientUpdates}
                        variant="contained"
                        color="primary"
                        sx={{ fontSize: '1rem' }}
                      >
                        שמירת עדכונים
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        onClick={handleGoBackClient}
                        color="inherit"
                        sx={{ fontSize: '1rem' }}
                        disabled={isUpdatingClient}
                      >
                        חזרה
                      </Button>
                      <Button
                        onClick={handleConfirmClientUpdates}
                        variant="contained"
                        color="primary"
                        sx={{ fontSize: '1rem' }}
                        ref={confirmClientButtonRef}
                        disabled={isUpdatingClient}
                      >
                        אישור סופי
                      </Button>
                    </>
                  )}
                </DialogActions>
              )}
            </MobileDialog>

            {/* =========================
                Delete Client Dialog
            ========================= */}
            <MobileDialog
              open={deleteDialogOpen}
              onClose={(event, reason) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
                handleCancelDeleteDialog();
              }}
              maxWidth="sm"
              fullWidth
              PaperProps={{ sx: { fontSize: { xs: '0.95rem', sm: '1.2rem' }, p: { xs: 1, sm: 2 } } }}
            >
              <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                {deleteStep === 1 ? 'אישור מחיקה' : 'הזן סיסמה לאישור סופי'}
              </DialogTitle>

              <DialogContent dividers>
                {deleteErrorMsg && <Alert severity="error" sx={{ mb: 2 }}>{deleteErrorMsg}</Alert>}
                {deleteSuccessMsg && <Alert severity="success" sx={{ mb: 2 }}>{deleteSuccessMsg}</Alert>}

                {/* Step 1 or Step 2 content */}
                {deleteStep === 1 ? (
                  <Typography sx={{ mb: 2 }}>
                    .האם אתה בטוח שברצונך למחוק את הלקוח? פעולה זו תמחק את הלקוח ותעביר אותו ללקוחות מחוקים
                  </Typography>
                ) : (
                  // If we have success, hide password fields entirely
                  !deleteSuccessMsg && (
                    <>
                      <Typography sx={{ mb: 2 }}>
                        כדי לאשר את המחיקה, הזן/י את הסיסמה שלך:
                      </Typography>
                      <TextField
                        label="סיסמה"
                        type={passwordVisible ? 'text' : 'password'}
                        fullWidth
                        value={deletePassword}
                        onChange={(e) => setDeletePassword(e.target.value)}
                        sx={{ mb: 2,  direction: 'ltr' }}
                        InputProps={{
                          endAdornment: (
                            <IconButton
                              onClick={() => setPasswordVisible(!passwordVisible)}
                              edge="end"
                            >
                              {passwordVisible ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          ),
                        }}
                      />
                    </>
                  )
                )}
              </DialogContent>

              {/* If deletion success, hide the actions entirely (dialog will close automatically). */}
              {!deleteSuccessMsg && (
                <DialogActions>
                  <Button
                    onClick={handleCancelDeleteDialog}
                    color="inherit"
                    disabled={deleteLoading}
                    sx={{ fontSize: '1rem' }}
                  >
                    ביטול
                  </Button>

                  {deleteStep === 1 ? (
                    <Button
                      onClick={handleConfirmDeleteStep1}
                      variant="contained"
                      color="error"
                      sx={{ fontSize: '1rem' }}
                    >
                      אישור 
                    </Button>
                  ) : (
                    <Button
                      onClick={handleConfirmDeleteStep2}
                      variant="contained"
                      color="error"
                      sx={{ fontSize: '1rem' }}
                      disabled={deleteLoading}
                    >
                      {deleteLoading ? 'מוחק...' : 'אישור סופי'}
                    </Button>
                  )}
                </DialogActions>
              )}
            </MobileDialog>
            
            {/* =========================
                NEW: Restore Client Dialog
            ========================= */}
            <MobileDialog
              open={restoreDialogOpen}
              onClose={(event, reason) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
                handleCancelRestore();
              }}
              maxWidth="sm"
              fullWidth
              PaperProps={{ sx: { fontSize: { xs: '0.95rem', sm: '1.2rem' }, p: { xs: 1, sm: 2 } } }}
            >
              <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 'bold' }}>אישור שחזור לקוח</DialogTitle>
              <DialogContent dividers>
                {restoreError && <Alert severity="error" sx={{ mb: 2 }}>{restoreError}</Alert>}
                {restoreSuccess && <Alert severity="success" sx={{ mb: 2 }}>{restoreSuccess}</Alert>}
                <Typography sx={{ mb: 2 }}>
                  האם אתה בטוח שברצונך לשחזר את הלקוח {client.ClientName}?
                </Typography>
                <Typography sx={{ mb: 2 }}>
                  פעולה זו תבטל את המחיקה ותהפוך את הלקוח לפעיל מחדש.
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCancelRestore} color="inherit" sx={{ fontSize: '1rem' }}>
                  ביטול
                </Button>
                <Button onClick={handleConfirmRestore} variant="contained" color="primary" sx={{ fontSize: '1rem' }} disabled={restoreLoading}>
                  {restoreLoading ? 'משחזר...' : 'אישור שחזור'}
                </Button>
              </DialogActions>
            </MobileDialog>
            
            {includeOrders && (
              <ExportOrderItemsDialog
                open={exportOrderItemsDialogOpen}
                onClose={() => setExportOrderItemsDialogOpen(false)}
                clientBusinessId={business_id}
                clientName={client?.ClientName || ''}
                orderLabel={orderLabel}
                orderPluralLabel={orderPluralLabel}
                productLabel={productLabel}
                productPluralLabel={productPluralLabel}
              />
            )}

            <MonthlyStatementDialog
              open={monthlyStatementDialogOpen}
              onClose={() => setMonthlyStatementDialogOpen(false)}
              clientBusinessId={business_id}
              clientName={client?.ClientName || ''}
              uiConfig={uiConfig}
              businessProfile={businessProfile}
            />

            {includeOrders && (
              <OrdersMonthlyStatementDialog
                open={ordersMonthlyStatementDialogOpen}
                onClose={() => setOrdersMonthlyStatementDialogOpen(false)}
                clientBusinessId={business_id}
                clientName={client?.ClientName || ''}
                uiConfig={uiConfig}
              />
            )}

            {includeOrders && (
              <>
                <Menu
                  anchorEl={orderActionMenuAnchor}
                  open={Boolean(orderActionMenuAnchor)}
                  onClose={handleOrderActionMenuClose}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <MenuItem onClick={() => handleOrderQuickAction('status')}>שינוי סטטוס</MenuItem>
                </Menu>
                <OrderStatusDialog
                  open={orderStatusDialogOpen}
                  onClose={() => setOrderStatusDialogOpen(false)}
                  order={orderActionMenuRow}
                  onSuccess={fetchClientOrders}
                  orderFieldsSchema={orderFieldsSchema}
                  orderLabel={orderLabel}
                />
              </>
            )}

            {/* ========== Mobile Transaction Detail Dialog ========== */}
            <MobileDialog
              open={Boolean(mobileTransactionDetail)}
              onClose={() => setMobileTransactionDetail(null)}
              maxWidth="md"
              fullWidth
            >
              {mobileTransactionDetail && (
                <>
                  <DialogTitle sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 1.5,
                    px: 2,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}>
                    <Typography sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                      פרטי תנועה
                    </Typography>
                    <IconButton onClick={() => setMobileTransactionDetail(null)} size="small">
                      <CloseIcon />
                    </IconButton>
                  </DialogTitle>
                  <DialogContent sx={{ p: 0 }}>
                    <TransactionDetailPanel
                      transaction={mobileTransactionDetail}
                      transactionFields={transactionFields}
                      transactionFieldsSchema={transactionFieldsSchema}
                      debtFieldsSchema={debtFieldsSchema}
                      clientFieldsSchema={clientFieldsSchema}
                      orderTransactionFieldsSchema={orderTransactionFieldsSchema}
                      onEdit={(transaction) => {
                        setMobileTransactionDetail(null);
                        setEditingTransaction(transaction);
                        setEditTransactionDialogOpen(true);
                      }}
                      onDelete={(transaction) => {
                        setMobileTransactionDetail(null);
                        setDeletingTransaction(transaction);
                        setDeleteTransactionDialogOpen(true);
                        setTransactionDeletePassword('');
                        setTransactionDeleteError('');
                      }}
                      userRole={role}
                    />
                  </DialogContent>
                </>
              )}
            </MobileDialog>

            {/* ========== Edit Transaction Dialog ========== */}
            <EditTransactionDialog
              open={editTransactionDialogOpen}
              onClose={() => {
                setEditTransactionDialogOpen(false);
                setEditingTransaction(null);
              }}
              transaction={editingTransaction}
              transactionFields={transactionFields}
              transactionFieldsSchema={transactionFieldsSchema}
              debtFieldsSchema={debtFieldsSchema}
              clientFieldsSchema={clientFieldsSchema}
              orderTransactionFieldsSchema={orderTransactionFieldsSchema}
              fetchTransactions={refreshTransactions}
              refreshClient={() => setRefreshKey(prev => prev + 1)}
              userProfile={userProfile}
            />

            {/* ========== Delete Transaction Dialog ========== */}
            <MobileDialog
              open={deleteTransactionDialogOpen}
              onClose={() => {
                if (!isDeletingTransaction) {
                  setDeleteTransactionDialogOpen(false);
                  setDeletingTransaction(null);
                  setTransactionDeletePassword('');
                  setTransactionDeleteError('');
                }
              }}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'error.main' }}>
                מחיקת תנועה
              </DialogTitle>
              <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                  {transactionDeleteError && (
                    <Alert severity="error" sx={{ fontWeight: 'bold' }}>{transactionDeleteError}</Alert>
                  )}
                  <Alert severity="warning" sx={{ fontWeight: 'bold' }}>
                    פעולה זו אינה ניתנת לביטול! התנועה תימחק לצמיתות והחוב יעודכן בהתאם.
                  </Alert>
                  {deletingTransaction && (
                    <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        סוג: {deletingTransaction.Type === 'Payment' ? 'תשלום' : deletingTransaction.Type === 'Charge' ? 'חיוב' : 'זיכוי'}
                      </Typography>
                      <Typography variant="body1">
                        סכום: {new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(deletingTransaction.Amount)}
                      </Typography>
                    </Box>
                  )}
                  <TextField
                    label="הזן סיסמה לאישור"
                    type="password"
                    value={transactionDeletePassword}
                    onChange={(e) => setTransactionDeletePassword(e.target.value)}
                    fullWidth
                    autoFocus
                    sx={{ '& .MuiInputLabel-root': { fontWeight: 'bold' } }}
                  />
                </Box>
              </DialogContent>
              <DialogActions sx={{ p: 2 }}>
                <Button
                  onClick={() => {
                    setDeleteTransactionDialogOpen(false);
                    setDeletingTransaction(null);
                    setTransactionDeletePassword('');
                    setTransactionDeleteError('');
                  }}
                  color="inherit"
                  disabled={isDeletingTransaction}
                  sx={{ fontWeight: 'bold' }}
                >
                  ביטול
                </Button>
                <Button
                  onClick={handleDeleteTransaction}
                  variant="contained"
                  color="error"
                  disabled={isDeletingTransaction || !transactionDeletePassword}
                  sx={{ fontWeight: 'bold' }}
                >
                  {isDeletingTransaction ? <CircularProgress size={24} /> : 'אשר מחיקה'}
                </Button>
              </DialogActions>
            </MobileDialog>
          </Container>
        </div>
      </ThemeProvider>
    </LocalizationProvider>
  );
};

export default ClientDetailPage; 